import React, { useState, useEffect, useRef } from 'react'
import { Routes, Route, useNavigate, useSearchParams } from 'react-router-dom'
import { PortalLayout } from '../components/Layout'
import { supabase, SIGNING_FEES, LCA_FLAT_FEE, fetchServices, buildFeesMap } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { sendAdminAlert, sendClientConfirmation, sendStatusUpdate } from '../lib/email'
import LoyaltyTierTracker from '../components/LoyaltyTierTracker'
import QuickServicePills, { getQuickServicePick } from '../components/QuickServicePills'
import { ChecklistSummaryClient } from '../components/PreAppointmentChecklist'
import JobMessages from '../components/JobMessages'
import ClientJobDetail from './ClientJobDetail'
import toast from 'react-hot-toast'

// ✅ ADDRESS AUTOCOMPLETE — OpenStreetMap Nominatim (FREE, no API key!!)
function AddressAutocomplete({ value, onChange, onSelect, placeholder, className }) {
  const [suggestions, setSuggestions] = React.useState([])
  const [open,        setOpen]        = React.useState(false)
  const [loading,     setLoading]     = React.useState(false)
  const timerRef = React.useRef(null)
  const justPickedRef = React.useRef(false)

  React.useEffect(() => {
    if (justPickedRef.current) { justPickedRef.current = false; setSuggestions([]); setOpen(false); return }
    if (!value || value.length < 4) { setSuggestions([]); setOpen(false); return }
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res  = await fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&countrycodes=us&q=${encodeURIComponent(value)}`, { headers:{ 'Accept-Language':'en-US' } })
        const data = await res.json()
        setSuggestions(data)
        setOpen(data.length > 0)
      } catch { setSuggestions([]) }
      finally { setLoading(false) }
    }, 400)
    return () => clearTimeout(timerRef.current)
  }, [value])

  function pick(item) {
    const a    = item.address || {}
    const road = a.road || a.pedestrian || a.footway || ''
    const num  = a.house_number || ''
    const addr = num ? `${num} ${road}` : road
    const city = a.city || a.town || a.village || a.hamlet || a.county || ''
    const st   = a.state || ''
    const zip  = a.postcode || ''
    const stAbbr = st.length === 2 ? st : (ST_ABBR[st] || st)
    justPickedRef.current = true
    onChange(addr)
    onSelect({ address:addr, city, state:stAbbr, zip })
    setSuggestions([])
    setOpen(false)
  }

  return (
    <div style={{ position:'relative' }}>
      <input className={className} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder || 'Start typing address...'} autoComplete="off"
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onFocus={() => suggestions.length > 0 && setOpen(true)} />
      {loading && <span style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', fontSize:11, color:'#4D4E8D' }}>🔍</span>}
      {open && suggestions.length > 0 && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1.5px solid #4D4E8D', borderRadius:10, zIndex:999, boxShadow:'0 8px 24px rgba(0,0,0,0.12)', marginTop:2, maxHeight:220, overflowY:'auto' }}>
          {suggestions.map((s, i) => (
            <button key={i} type="button" onMouseDown={() => pick(s)}
              style={{ width:'100%', padding:'10px 14px', background:'none', border:'none', cursor:'pointer', textAlign:'left', fontSize:12, color:'#151715', borderBottom:'1px solid #EEEFF1', fontFamily:'inherit' }}
              onMouseEnter={e => e.currentTarget.style.background='#EEF0F8'}
              onMouseLeave={e => e.currentTarget.style.background='none'}>
              📍 {s.display_name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const ST_ABBR = { 'Alabama':'AL','Alaska':'AK','Arizona':'AZ','Arkansas':'AR','California':'CA','Colorado':'CO','Connecticut':'CT','Delaware':'DE','Florida':'FL','Plus Jakarta Sans':'GA','Hawaii':'HI','Idaho':'ID','Illinois':'IL','Indiana':'IN','Iowa':'IA','Kansas':'KS','Kentucky':'KY','Louisiana':'LA','Maine':'ME','Maryland':'MD','Massachusetts':'MA','Michigan':'MI','Minnesota':'MN','Mississippi':'MS','Missouri':'MO','Montana':'MT','Nebraska':'NE','Nevada':'NV','New Hampshire':'NH','New Jersey':'NJ','New Mexico':'NM','New York':'NY','North Carolina':'NC','North Dakota':'ND','Ohio':'OH','Oklahoma':'OK','Oregon':'OR','Pennsylvania':'PA','Rhode Island':'RI','South Carolina':'SC','South Dakota':'SD','Tennessee':'TN','Texas':'TX','Utah':'UT','Vermont':'VT','Virginia':'VA','Washington':'WA','West Virginia':'WV','Wisconsin':'WI','Wyoming':'WY' }

const STRIPE_PAYMENT_LINK = 'https://buy.stripe.com/8x2cN5esR2NTcEM29DgA804'
const DRAFT_KEY = 'lca_client_order_draft'
const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY']
const LANGUAGES = ['No preference','English','Spanish','French','Portuguese','Mandarin','Cantonese','Vietnamese','Korean','Arabic','Haitian Creole','Russian','German','Italian','Japanese','Tagalog']

// ✅ FIX 3 — Sector types (placeholder added in JSX)
const SECTOR_TYPES = [
  { value:'title',      label:'Title Company / Lender' },
  { value:'attorney',   label:'Attorney / Law Firm'    },
  { value:'dealership', label:'Auto Dealership'        },
  { value:'hospital',   label:'Hospital / Medical'     },
  { value:'other',      label:'Other'                  },
]

const STATUS_CONFIG = {
  new:                  { label:'Order Received',        color:'#7C6A2E', bg:'#EEF0F8', border:'#4D4E8D', dot:'#4D4E8D' },
  awaiting_assignment:  { label:'Finding Your Notary',   color:'#7C6A2E', bg:'#EEF0F8', border:'#4D4E8D', dot:'#F59E0B' },
  assigned:             { label:'Notary Notified',       color:'#1A4A6B', bg:'#EBF4FF', border:'#3B82F6', dot:'#F59E0B' },
  accepted:             { label:'Notary Confirmed',      color:'#1A4A2E', bg:'#EAF2EE', border:'#5E8A72', dot:'#5E8A72' },
  on_the_way:           { label:'Notary On The Way',     color:'#3A3D40', bg:'#EBF4FF', border:'#185FA5', dot:'#185FA5' },
  arrived:              { label:'Notary Arrived',        color:'#1A4A3E', bg:'#E8F5F0', border:'#2D7A6A', dot:'#2D7A6A' },
  completed:            { label:'Signing Complete',      color:'#1A4A2E', bg:'#EAF2EE', border:'#5E8A72', dot:'#5BA882' },
  scanbacks_uploaded:   { label:'Documents Uploaded',    color:'#1A4A3E', bg:'#E8F5F0', border:'#2D7A6A', dot:'#2D7A6A' },
  client_approved:      { label:'Final Review Complete', color:'#3A1A5E', bg:'#F0EBF8', border:'#6A3A9A', dot:'#6A3A9A' },
  payment_received:     { label:'Order Complete',        color:'#1A4A2E', bg:'#EAF2EE', border:'#5E8A72', dot:'#5E8A72' },
  notary_paid:          { label:'Order Complete',        color:'#1A4A2E', bg:'#EAF2EE', border:'#5E8A72', dot:'#5E8A72' },
  closed:               { label:'Closed',                color:'#2A2A2A', bg:'#F4F4F4', border:'#8A8A8A', dot:'#8A8A8A' },
  cancelled:            { label:'Cancelled',             color:'#6B2020', bg:'#FDF2F2', border:'#E24B4A', dot:'#E24B4A' },
  declined:             { label:'Reassigning Notary',    color:'#7C6A2E', bg:'#EEF0F8', border:'#4D4E8D', dot:'#F59E0B' },
}

function StatusRibbon({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new
  return (
    <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:cfg.bg, border:`1px solid ${cfg.border}40`, borderRadius:20, padding:'4px 12px' }}>
      <div style={{ width:7, height:7, borderRadius:'50%', background:cfg.dot, boxShadow:`0 0 6px ${cfg.dot}` }} />
      <span style={{ fontSize:11, fontWeight:600, color:cfg.color, letterSpacing:'0.04em' }}>{cfg.label}</span>
    </div>
  )
}

const LUXURY_STYLES = `
  :focus-visible { outline:2px solid #4D4E8D; outline-offset:3px; border-radius:4px; }
  .lca-skip { position:absolute; top:-100px; left:16px; background:#4D4E8D; color:#fff; padding:8px 16px; border-radius:8px; font-size:13px; font-weight:600; text-decoration:none; z-index:9999; transition:top .2s; }
  .lca-skip:focus { top:16px; }
  .lux-hero-btn { display:flex; align-items:center; justify-content:center; gap:12px; width:100%; padding:18px 20px; background:linear-gradient(135deg,#4D4E8D,#41427a); border:none; border-radius:16px; cursor:pointer; transition:all 0.25s; box-shadow:0 8px 32px rgba(21,23,21,0.35); font-family:'Plus Jakarta Sans',sans-serif; font-size:20px; font-weight:600; color:#fff; letter-spacing:0.04em; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  @media(max-width:480px){ .lux-hero-btn { font-size:16px; padding:16px 14px; gap:8px; letter-spacing:0.02em; } }
  .lux-hero-btn:hover { transform:translateY(-2px); box-shadow:0 12px 40px rgba(21,23,21,0.45); }
  .lux-job-card { border:1px solid #E2E4E7; border-radius:16px; overflow:hidden; margin-bottom:16px; background:#fff; transition:box-shadow 0.2s; }
  .lux-job-card:hover { box-shadow:0 4px 24px rgba(21,23,21,0.08); }
  .lux-field-label { font-size:9px; color:#A2A6AA; letter-spacing:0.1em; font-weight:600; margin-bottom:3px; text-transform:uppercase; }
  .lux-field-value { font-size:13px; color:#151715; font-weight:500; }
  .lux-vetted-badge { display:inline-flex; align-items:center; gap:5px; background:linear-gradient(135deg,#4D4E8D,#41427a); border:1px solid rgba(77,78,141,0.3); border-radius:20px; padding:3px 10px; font-size:10px; font-weight:600; color:#fff; letter-spacing:0.04em; }
  .lux-tracker { display:flex; flex-direction:column; gap:10px; padding:16px 20px; }
  .lux-tracker-step { display:flex; align-items:center; gap:12px; }
  .lux-tracker-dot { width:26px; height:26px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:600; flex-shrink:0; }
  .lux-tracker-line { width:2px; height:16px; margin-left:12px; }
  .lux-form-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
  @media(max-width:640px){ .lux-form-grid { grid-template-columns:1fr; } }
  .lux-full { grid-column:1/-1; }
  .lux-input { width:100%; padding:11px 14px; border:1.5px solid #D7DADE; border-radius:10px; font-size:14px; color:#151715; background:#F7F8F9; transition:all 0.2s; }
  .lux-input:focus { border-color:#4D4E8D; box-shadow:0 0 0 3px rgba(77,78,141,0.12); outline:none; background:#fff; }
  .lux-label { font-size:11px; font-weight:600; color:#5C6065; letter-spacing:0.06em; margin-bottom:5px; display:block; }
  .lux-select { width:100%; padding:11px 14px; border:1.5px solid #D7DADE; border-radius:10px; font-size:14px; color:#151715; background:#F7F8F9; cursor:pointer; }
  .lux-select:focus { border-color:#4D4E8D; outline:none; }
  .lux-sector-panel { background:#F5F6F7; border:1px solid #E2E4E7; border-radius:12px; padding:16px; margin-top:4px; }
  .lux-success-wrap { min-height:60vh; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; padding:40px 20px; }
  .lux-success-ring { width:80px; height:80px; border-radius:50%; background:linear-gradient(135deg,#4D4E8D,#41427a); display:flex; align-items:center; justify-content:center; font-size:36px; margin:0 auto 24px; box-shadow:0 8px 32px rgba(77,78,141,0.4); animation:lux-pop 0.4s cubic-bezier(0.175,0.885,0.32,1.275); }
  @keyframes lux-pop { from { transform:scale(0); opacity:0; } to { transform:scale(1); opacity:1; } }
  .lux-recent-item { display:flex; align-items:center; gap:10px; padding:8px 12px; border-radius:8px; cursor:pointer; transition:background 0.15s; }
  .lux-recent-item:hover { background:#F5F6F7; }
  .lux-table-wrap { overflow-x:auto; -webkit-overflow-scrolling:touch; }
  .lux-notary-card { background:linear-gradient(135deg,#4D4E8D,#41427a); border-radius:14px; padding:16px; display:flex; align-items:center; gap:14px; }
  .lux-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:24px; }
  @media(max-width:600px){ .lux-stats { grid-template-columns:1fr 1fr; } }
  .lux-stat-card { background:linear-gradient(135deg,#4D4E8D,#41427a); border-radius:14px; padding:18px; cursor:pointer; transition:all 0.2s; border:1px solid rgba(77,78,141,0.15); }
  .lux-stat-card:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(21,23,21,0.25); border-color:rgba(77,78,141,0.4); }
  .lux-cal-btn { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; border-radius:8px; font-size:11px; font-weight:600; cursor:pointer; border:1px solid #E2E4E7; background:#F5F6F7; color:#3A3D40; text-decoration:none; transition:all 0.15s; }
  .lux-cal-btn:hover { border-color:#4D4E8D; color:#151715; background:#EEF0F8; }
  .lux-toggle-track { width:44px; height:24px; border-radius:12px; border:none; cursor:pointer; position:relative; transition:background 0.2s; flex-shrink:0; }
  .lux-toggle-thumb { width:20px; height:20px; border-radius:50%; background:#fff; position:absolute; top:2px; transition:left 0.2s; box-shadow:0 1px 4px rgba(0,0,0,0.2); }
  .lux-tooltip-wrap { position:relative; display:inline-flex; align-items:center; }
  .lux-tooltip-box { position:fixed; background:#4D4E8D; color:#fff; font-size:11px; padding:8px 12px; border-radius:8px; white-space:nowrap; text-align:center; line-height:1.5; box-shadow:0 4px 16px rgba(0,0,0,0.3); z-index:9999; border:1px solid rgba(77,78,141,0.3); pointer-events:none; transform:translateX(-50%); }
  .lux-tooltip-box::after { display:none; }
  .lux-cal-grid { display:grid; grid-template-columns:repeat(7,1fr); gap:2px; }
  .lux-cal-day { min-height:56px; padding:4px; border-radius:6px; background:#F5F6F7; border:1px solid #E2E4E7; font-size:11px; }
  .lux-cal-day.today { border-color:#4D4E8D; background:#EEF0F8; }
  .lux-cal-day.has-signing { background:#EBF4FF; border-color:#3A3D40; cursor:pointer; }
  .lux-cal-day.has-signing:hover { background:#D6E9FF; }
  .lux-cal-event { background:#4D4E8D; color:#fff; font-size:9px; padding:2px 4px; border-radius:3px; margin-top:2px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
`

// ✅ FIX 7 — Clickable stat card
function StatCard({ label, value, sub, onClick }) {
  return (
    <div className="lux-stat-card" onClick={onClick} role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={e => e.key === 'Enter' && onClick && onClick()}
      aria-label={onClick ? `${label}: ${value}. Click to view.` : undefined}>
      <div style={{ fontSize:10, color:'rgba(255,255,255,0.6)', letterSpacing:'0.1em', fontWeight:600, marginBottom:6 }}>{label}</div>
      <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:26, fontWeight:600, color:'#fff', lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', marginTop:4 }}>{sub}</div>}
    </div>
  )
}

// ✅ FIX 8 — Signings Calendar
function SigningsCalendar({ jobs }) {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedJob, setSelectedJob] = useState(null)
  const year  = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const firstDay    = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const today       = new Date()
  const monthName   = currentDate.toLocaleString('en-US', { month:'long', year:'numeric' })

  const navigate = useNavigate()

  const jobsByDay = {}
  jobs.forEach(j => {
    if (!j.scheduled_at) return
    const d = new Date(j.scheduled_at)
    if (d.getFullYear() === year && d.getMonth() === month) {
      const day = d.getDate()
      if (!jobsByDay[day]) jobsByDay[day] = []
      jobsByDay[day].push(j)
    }
  })

  const cells = []
  for (let i = 0; i < firstDay; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
          style={{ background:'none', border:'1px solid #E2E4E7', borderRadius:8, padding:'6px 12px', cursor:'pointer', fontSize:13, color:'#151715' }}>←</button>
        <span style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:18, fontWeight:600, color:'#151715' }}>{monthName}</span>
        <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
          style={{ background:'none', border:'1px solid #E2E4E7', borderRadius:8, padding:'6px 12px', cursor:'pointer', fontSize:13, color:'#151715' }}>→</button>
      </div>
      <div className="lux-cal-grid" style={{ marginBottom:4 }}>
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
          <div key={d} style={{ textAlign:'center', fontSize:10, fontWeight:600, color:'#A2A6AA', letterSpacing:'0.06em', padding:'4px 0' }}>{d}</div>
        ))}
      </div>
      <div className="lux-cal-grid">
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />
          const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear()
          const dayJobs = jobsByDay[day] || []
          return (
            <div key={day}
              className={`lux-cal-day${isToday ? ' today' : ''}${dayJobs.length > 0 ? ' has-signing' : ''}`}
              onClick={() => dayJobs.length > 0 && setSelectedJob(dayJobs[0])}>
              <div style={{ fontWeight:isToday?700:400, color:isToday?'#41427a':'#151715', fontSize:12 }}>{day}</div>
              {dayJobs.slice(0,2).map((j, idx) => (
                <div key={idx} className="lux-cal-event" title={j.borrower_name}>{j.borrower_name || 'Signing'}</div>
              ))}
            </div>
          )
        })}
      </div>
      {selectedJob && (
        <div style={{ marginTop:16, padding:16, background:'#4D4E8D', borderRadius:12, border:'1px solid rgba(77,78,141,0.3)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
            <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:16, color:'#fff', fontWeight:600 }}>{selectedJob.signing_type}</div>
            <button onClick={() => setSelectedJob(null)} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.4)', cursor:'pointer', fontSize:16 }}>✕</button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, fontSize:12, color:'rgba(255,255,255,0.7)' }}>
            <div><span style={{ color:'rgba(255,255,255,0.4)', fontSize:10 }}>JOB #</span><br/><span style={{ color:'#fff', fontWeight:600 }}>{selectedJob.job_number}</span></div>
            <div><span style={{ color:'rgba(255,255,255,0.4)', fontSize:10 }}>BORROWER</span><br/>{selectedJob.borrower_name||'—'}</div>
            <div><span style={{ color:'rgba(255,255,255,0.4)', fontSize:10 }}>TIME</span><br/>{selectedJob.scheduled_at ? new Date(selectedJob.scheduled_at).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'}) : 'TBD'}</div>
            <div><span style={{ color:'rgba(255,255,255,0.4)', fontSize:10 }}>LOCATION</span><br/>{selectedJob.signing_city}, {selectedJob.signing_state}</div>
          </div>
          <div style={{ marginTop:8 }}><StatusRibbon status={selectedJob.status} /></div>
          <button
            onClick={() => navigate(`/client/jobs/${selectedJob.id}`)}
            className="btn btn-primary btn-sm"
            style={{ marginTop:12, width:'100%', fontSize:12 }}>
            📋 View Full Order Details →
          </button>
        </div>
      )}
      {jobs.filter(j => j.scheduled_at).length === 0 && (
        <div style={{ textAlign:'center', padding:'32px 0', color:'#A2A6AA', fontSize:13 }}>📅 No signings scheduled yet — your calendar will fill up soon!!</div>
      )}
    </div>
  )
}

// ✅ FIX 8+9 — Updated NAV with Calendar + All Orders
const NAV = [
  { section:'Orders'  },
  { to:'/client',            icon:'◉',  label:'Dashboard',         end:true },
  { to:'/client/new',        icon:'✍️',  label:'New Order'                   },
  { to:'/client/orders',     icon:'◈',  label:'My Orders'                    },
  { to:'/client/calendar',   icon:'📅', label:'Signings Calendar'            },
  { to:'/client/all-orders', icon:'📋', label:'All Orders'                   },
  { section:'Billing' },
  { to:'/client/invoices',   icon:'◆',  label:'Invoices'                     },
  { section:'Account' },
  { to:'/client/profile',    icon:'◑',  label:'Company Profile'              },
]

function safeParseJobs(jobs) {
  return (jobs || []).map(j => ({
    ...j,
    office_prefs: j.office_prefs && typeof j.office_prefs === 'object' ? j.office_prefs : {},
  }))
}

function OrderNotes({ jobId, initialNotes }) {
  const [notes, setNotes]   = useState(initialNotes || '')
  const [saved, setSaved]   = useState(!!initialNotes)
  const [saving, setSaving] = useState(false)
  const [open, setOpen]     = useState(true)

  async function saveNotes() {
    setSaving(true)
    try {
      await supabase.from('jobs').update({ order_notes: notes }).eq('id', jobId)
      setSaved(true)
      toast.success('Notes saved!!')
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  return (
    <div style={{ borderTop:'1px solid #E2E4E7' }}>
      <button onClick={() => setOpen(o => !o)} style={{ width:'100%', background:'none', border:'none', padding:'10px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer', fontSize:12, color:'#151715', fontWeight:500 }}>
        <span>💬 Order Notes & Special Instructions {saved && notes && <span style={{ color:'#5E8A72', fontSize:10 }}>✓ Saved</span>}</span>
        <span style={{ color:'#A2A6AA' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ padding:'0 20px 16px' }}>
          <textarea value={notes} onChange={e => { setNotes(e.target.value); setSaved(false) }}
            placeholder="Add special instructions for your notary..."
            rows={3} style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #D7DADE', borderRadius:8, fontSize:12, resize:'vertical', fontFamily:'inherit', marginBottom:8 }} />
          <button onClick={saveNotes} disabled={saving || saved} className="btn btn-primary btn-sm" style={{ fontSize:11 }}>
            {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Notes'}
          </button>
        </div>
      )}
    </div>
  )
}

function OfficePrefs({ userId, currentPrefs }) {
  const [prefs, setPrefs] = useState({ blue_ink:false, no_staples:false, gps_checkin:false, borrower_id:false, call_before:false, scan_backs:false, custom_note:'', ...currentPrefs })
  const [saving, setSaving] = useState(false)
  const [saved,  setSaved]  = useState(false)

  const TOGGLES = [
    { key:'blue_ink',    icon:'🖊️', label:'Blue Ink Only',                 desc:'Notary must use blue ink for all signatures' },
    { key:'no_staples',  icon:'📎', label:'No Staples — Use Binder Clips', desc:'Documents must be secured with binder clips only' },
    { key:'gps_checkin', icon:'📍', label:'GPS Check-In Required',         desc:'Notary must check in via GPS upon arrival' },
    { key:'borrower_id', icon:'🪪', label:'Upload Borrower ID Photo',      desc:"Notary must upload a photo of the borrower's ID" },
    { key:'call_before', icon:'📞', label:'Call 30 Min Before Arrival',    desc:'Notary must call borrower 30 minutes before arriving' },
    { key:'scan_backs',  icon:'📄', label:'Scan-Backs Required',           desc:'Notary must upload scanned copies of all signed documents' },
  ]

  async function savePrefs() {
    setSaving(true)
    try {
      await supabase.from('profiles').update({ office_prefs: prefs }).eq('id', userId)
      setSaved(true)
      toast.success('Office instructions saved!!')
      setTimeout(() => setSaved(false), 3000)
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
        {TOGGLES.map(t => (
          <div key={t.key} onClick={() => setPrefs(p => ({ ...p, [t.key]: !p[t.key] }))}
            style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 14px', border:`1.5px solid ${prefs[t.key]?'#4D4E8D':'#E2E4E7'}`, borderRadius:10, background:prefs[t.key]?'#F5F6F7':'#F7F8F9', cursor:'pointer', transition:'all 0.15s' }}>
            <div style={{ fontSize:20, flexShrink:0 }}>{t.icon}</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:12, fontWeight:600, color:'#151715' }}>{t.label}</div>
              <div style={{ fontSize:10, color:'#5C6065', marginTop:2 }}>{t.desc}</div>
            </div>
            <div style={{ width:36, height:20, borderRadius:10, background:prefs[t.key]?'#4D4E8D':'#E2E4E7', position:'relative', flexShrink:0, transition:'background 0.2s' }}>
              <div style={{ width:16, height:16, borderRadius:'50%', background:'#fff', position:'absolute', top:2, left:prefs[t.key]?18:2, transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
            </div>
          </div>
        ))}
      </div>
      <div className="form-group" style={{ marginBottom:12 }}>
        <label className="lux-label">ADDITIONAL STANDING INSTRUCTIONS</label>
        <textarea value={prefs.custom_note} onChange={e => setPrefs(p => ({ ...p, custom_note: e.target.value }))}
          placeholder="Any additional office-wide instructions for all your notaries..."
          rows={3} style={{ width:'100%', padding:'10px 14px', border:'1.5px solid #D7DADE', borderRadius:10, fontSize:12, fontFamily:'inherit', resize:'vertical' }} />
      </div>
      <button onClick={savePrefs} disabled={saving || saved} className="btn btn-primary" style={{ fontSize:12 }}>
        {saving ? 'Saving...' : saved ? '✓ Saved!!' : '💾 Save Office Instructions'}
      </button>
    </div>
  )
}

function AddressBook({ userId, currentBook, onSelect }) {
  const [book,   setBook]   = useState(currentBook || [])
  const [adding, setAdding] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form,   setForm]   = useState({ label:'', contact_name:'', address:'', city:'', state:'FL', zip:'', phone:'', email:'' })

  async function saveEntry() {
    if (!form.label || !form.address) { toast.error('Please enter a label and address!!'); return }
    setSaving(true)
    try {
      const newBook = [...book, { ...form, id: Date.now().toString() }]
      await supabase.from('profiles').update({ address_book: newBook }).eq('id', userId)
      setBook(newBook)
      setForm({ label:'', contact_name:'', address:'', city:'', state:'FL', zip:'', phone:'', email:'' })
      setAdding(false)
      toast.success('Address saved!!')
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  async function deleteEntry(id) {
    const newBook = book.filter(e => e.id !== id)
    await supabase.from('profiles').update({ address_book: newBook }).eq('id', userId)
    setBook(newBook)
    toast.success('Address removed!!')
  }

  return (
    <div>
      {book.length === 0 && !adding && <div style={{ fontSize:12, color:'#A2A6AA', marginBottom:12 }}>No saved addresses yet!!</div>}
      {book.map(entry => (
        <div key={entry.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid #EEEFF1' }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'#151715' }}>{entry.label}</div>
            {entry.contact_name && <div style={{ fontSize:11, color:'#5C6065' }}>👤 {entry.contact_name}</div>}
            <div style={{ fontSize:11, color:'#5C6065' }}>📍 {entry.address}, {entry.city}, {entry.state} {entry.zip}</div>
          </div>
          <div style={{ display:'flex', gap:6, flexShrink:0 }}>
            {onSelect && <button onClick={() => onSelect(entry)} className="btn btn-primary btn-sm" style={{ fontSize:10 }}>Use This →</button>}
            <button onClick={() => deleteEntry(entry.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#E24B4A', fontSize:14 }}>✕</button>
          </div>
        </div>
      ))}
      {adding ? (
        <div style={{ background:'#F5F6F7', borderRadius:10, padding:14, marginTop:10 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
            <div><div style={{ fontSize:10, fontWeight:600, color:'#5C6065', marginBottom:3 }}>LABEL *</div><input value={form.label} onChange={e => setForm(f=>({...f,label:e.target.value}))} placeholder="e.g. Main Office" style={{ width:'100%', padding:'7px 10px', border:'1px solid #D7DADE', borderRadius:7, fontSize:12 }} /></div>
            <div><div style={{ fontSize:10, fontWeight:600, color:'#5C6065', marginBottom:3 }}>CONTACT NAME</div><input value={form.contact_name} onChange={e => setForm(f=>({...f,contact_name:e.target.value}))} placeholder="Jane Doe" style={{ width:'100%', padding:'7px 10px', border:'1px solid #D7DADE', borderRadius:7, fontSize:12 }} /></div>
            <div style={{ gridColumn:'1/-1' }}><div style={{ fontSize:10, fontWeight:600, color:'#5C6065', marginBottom:3 }}>ADDRESS *</div><input value={form.address} onChange={e => setForm(f=>({...f,address:e.target.value}))} placeholder="123 Any Street, Suite 100" style={{ width:'100%', padding:'7px 10px', border:'1px solid #D7DADE', borderRadius:7, fontSize:12 }} /></div>
            <div><div style={{ fontSize:10, fontWeight:600, color:'#5C6065', marginBottom:3 }}>CITY</div><input value={form.city} onChange={e => setForm(f=>({...f,city:e.target.value}))} placeholder="City" style={{ width:'100%', padding:'7px 10px', border:'1px solid #D7DADE', borderRadius:7, fontSize:12 }} /></div>
            <div><div style={{ fontSize:10, fontWeight:600, color:'#5C6065', marginBottom:3 }}>ZIP</div><input value={form.zip} onChange={e => setForm(f=>({...f,zip:e.target.value.replace(/\D/g,'')}))} placeholder="Zip Code" maxLength={5} style={{ width:'100%', padding:'7px 10px', border:'1px solid #D7DADE', borderRadius:7, fontSize:12 }} /></div>
            <div><div style={{ fontSize:10, fontWeight:600, color:'#5C6065', marginBottom:3 }}>PHONE</div><input value={form.phone} onChange={e => setForm(f=>({...f,phone:e.target.value}))} placeholder="(555) 123-4567" style={{ width:'100%', padding:'7px 10px', border:'1px solid #D7DADE', borderRadius:7, fontSize:12 }} /></div>
            <div><div style={{ fontSize:10, fontWeight:600, color:'#5C6065', marginBottom:3 }}>EMAIL</div><input value={form.email} onChange={e => setForm(f=>({...f,email:e.target.value}))} placeholder="jane@company.com" style={{ width:'100%', padding:'7px 10px', border:'1px solid #D7DADE', borderRadius:7, fontSize:12 }} /></div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={saveEntry} disabled={saving} className="btn btn-primary btn-sm" style={{ fontSize:11 }}>{saving ? 'Saving...' : '💾 Save Address'}</button>
            <button onClick={() => setAdding(false)} className="btn btn-outline btn-sm" style={{ fontSize:11 }}>Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} className="btn btn-outline btn-sm" style={{ fontSize:11, marginTop:10 }}>+ Add Address</button>
      )}
    </div>
  )
}

function RecentFileItem({ job: j }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ borderBottom:'1px solid #EEEFF1' }}>
      <div className="lux-recent-item" onClick={() => setOpen(o => !o)} style={{ justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:32, height:32, borderRadius:8, background:open?'#151715':'#F5F6F7', border:'1px solid #E2E4E7', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0, transition:'all 0.15s' }}>
            <span style={{ color:open?'#4D4E8D':'#5C6065' }}>{open ? '▼' : '📋'}</span>
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:12, fontWeight:500, color:'#151715', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{j.borrower_name||'Borrower'} — {j.signing_type}</div>
            <div style={{ fontSize:11, color:'#A2A6AA' }}>{j.job_number} · {new Date(j.created_at).toLocaleString()}</div>
          </div>
        </div>
        <StatusRibbon status={j.status} />
      </div>
      {open && (
        <div style={{ background:'#F5F6F7', padding:'12px 16px', fontSize:12, display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
          <div><span style={{ color:'#A2A6AA' }}>Job #</span><br/><span style={{ fontWeight:500, color:'#41427a' }}>{j.job_number}</span></div>
          <div><span style={{ color:'#A2A6AA' }}>Service</span><br/><span style={{ fontWeight:500 }}>{j.signing_type}</span></div>
          <div><span style={{ color:'#A2A6AA' }}>Borrower</span><br/><span style={{ fontWeight:500 }}>{j.borrower_name||'—'}</span></div>
          <div><span style={{ color:'#A2A6AA' }}>Location</span><br/><span style={{ fontWeight:500 }}>{j.signing_city}, {j.signing_state}</span></div>
          <div><span style={{ color:'#A2A6AA' }}>Appointment</span><br/><span style={{ fontWeight:500 }}>{j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : 'TBD'}</span></div>
          <div><span style={{ color:'#A2A6AA' }}>Amount</span><br/><span style={{ fontWeight:600, color:'#5E8A72' }}>${j.total_fee} All-Inclusive</span></div>
        </div>
      )}
    </div>
  )
}

function StarRating({ jobId, notaryId, clientId, onRated, compact }) {
  const [rating, setRating]       = useState(0)
  const [hover, setHover]         = useState(0)
  const [comment, setComment]     = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [saving, setSaving]       = useState(false)

  useEffect(() => {
    supabase.from('jobs').select('client_rating, client_rating_comment').eq('id', jobId).single()
      .then(({ data }) => { if (data?.client_rating) { setRating(data.client_rating); setComment(data.client_rating_comment||''); setSubmitted(true) } })
  }, [jobId])

  async function submitRating(stars) {
    const r = typeof stars === 'number' ? stars : rating
    if (!r) { toast.error('Please select a star rating!!'); return }
    setRating(r)
    setSaving(true)
    try {
      await supabase.from('jobs').update({ client_rating:r, client_rating_comment:comment||null, client_rated_at:new Date().toISOString() }).eq('id', jobId)
      // Best-effort: roll up the notary's average (won't block the client's rating if it can't)
      try {
        const { data: jobs } = await supabase.from('jobs').select('client_rating').eq('notary_id', notaryId).not('client_rating','is',null)
        if (jobs?.length) {
          const avg = Math.round((jobs.reduce((a,j) => a + j.client_rating, 0) / jobs.length) * 10) / 10
          await supabase.from('notaries').update({ avg_rating: avg }).eq('id', notaryId)
        }
      } catch (rollupErr) { /* rating already saved to the job; rollup is optional */ }
      setSubmitted(true)
      if (onRated) onRated(r)
      toast.success('Thank you for rating your notary!! 🌟')
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  if (compact) {
    if (submitted) return <div style={{ textAlign:'center' }}><div style={{ fontSize:22, color:'#4D4E8D', letterSpacing:2 }}>{'★'.repeat(rating)}{'☆'.repeat(5-rating)}</div><div style={{ fontSize:10, color:'#5E8A72', fontWeight:600, marginTop:4 }}>✓ Rated {rating}/5</div></div>
    return (
      <div style={{ textAlign:'center' }}>
        <div style={{ display:'flex', gap:3, justifyContent:'center', marginBottom:4 }}>
          {[1,2,3,4,5].map(s => <button key={s} type="button" onMouseEnter={() => setHover(s)} onMouseLeave={() => setHover(0)} onClick={() => setRating(s)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:22, color:(hover||rating)>=s?'#4D4E8D':'#D7DADE', padding:0, lineHeight:1, transition:'color 0.1s' }}>★</button>)}
        </div>
        <div style={{ fontSize:10, color:'#41427a', fontWeight:500 }}>Rate your notary</div>
        {rating > 0 && (
          <div style={{ marginTop:8, textAlign:'left' }}>
            <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Add feedback for your notary (optional)..." rows={2} style={{ width:'100%', padding:'8px 10px', border:'1.5px solid #D7DADE', borderRadius:8, fontSize:12, resize:'none', fontFamily:'inherit', marginBottom:8, boxSizing:'border-box' }} />
            <button className="btn btn-primary btn-sm" onClick={() => submitRating(rating)} disabled={saving} style={{ width:'100%' }}>{saving ? 'Submitting...' : 'Submit Rating'}</button>
          </div>
        )}
      </div>
    )
  }

  if (submitted) return <div style={{ padding:'14px 20px', borderTop:'1px solid #E2E4E7', background:'#F7F8F9', textAlign:'center' }}><div style={{ fontSize:22, marginBottom:4 }}>{'⭐'.repeat(rating)}</div><div style={{ fontSize:12, color:'#5E8A72', fontWeight:600 }}>Thank you for rating your notary!!</div></div>

  return (
    <div style={{ padding:'14px 20px', borderTop:'1px solid #E2E4E7', background:'#FAFDF5' }}>
      <div style={{ fontSize:12, fontWeight:600, color:'#151715', marginBottom:10 }}>⭐ How was your notary??</div>
      <div style={{ display:'flex', gap:6, marginBottom:10 }}>
        {[1,2,3,4,5].map(s => <button key={s} type="button" onMouseEnter={() => setHover(s)} onMouseLeave={() => setHover(0)} onClick={() => setRating(s)} aria-label={`Rate ${s} star${s>1?'s':''}`} style={{ background:'none', border:'none', cursor:'pointer', fontSize:28, color:s<=(hover||rating)?'#4D4E8D':'#E2E4E7', transition:'color 0.15s', padding:2 }}>★</button>)}
        {rating > 0 && <span style={{ fontSize:12, color:'#5C6065', alignSelf:'center', marginLeft:4 }}>{['','Poor','Fair','Good','Great','Excellent!!'][rating]}</span>}
      </div>
      <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Tell us about your experience (optional)..." rows={2} style={{ width:'100%', padding:'8px 12px', border:'1.5px solid #D7DADE', borderRadius:8, fontSize:12, resize:'none', marginBottom:10, fontFamily:'inherit' }} />
      <button className="btn btn-primary btn-sm" onClick={() => submitRating()} disabled={saving || !rating}>{saving ? 'Submitting...' : 'Submit Rating'}</button>
    </div>
  )
}

function LuxTracker({ status }) {
  const displaySteps = [
    { key:'new',                label:'Order Received'   },
    { key:'assigned',           label:'Notary Notified'  },
    { key:'accepted',           label:'Notary Confirmed' },
    { key:'arrived',            label:'Notary Arrived'   },
    { key:'completed',          label:'Signing Complete' },
    { key:'scanbacks_uploaded', label:'Documents Uploaded' },
    { key:'client_approved',    label:'Final Review'     },
    { key:'closed',             label:'Order Complete'   },
  ]
  // Map every canonical status onto a visible step
  const STEP_FOR_STATUS = {
    new:'new', awaiting_assignment:'new',
    assigned:'assigned', declined:'assigned',
    accepted:'accepted', on_the_way:'accepted',
    arrived:'arrived',
    completed:'completed',
    scanbacks_uploaded:'scanbacks_uploaded',
    client_approved:'client_approved', payment_received:'closed', notary_paid:'closed', closed:'closed',
  }
  const displayIdx = displaySteps.findIndex(s => s.key === (STEP_FOR_STATUS[status] || 'new'))
  return (
    <div className="lux-tracker">
      {displaySteps.map((s, i) => {
        const done = i < displayIdx, current = i === displayIdx
        return (
          <div key={s.key}>
            <div className="lux-tracker-step">
              <div className="lux-tracker-dot" style={{ background:done?'#5E8A72':current?'#4D4E8D':'#E2E4E7', color:done||current?'#fff':'#A2A6AA' }}>{done ? '✓' : i+1}</div>
              <span style={{ fontSize:13, color:current?'#151715':done?'#5E8A72':'#A2A6AA', fontWeight:current?600:400 }}>
                {s.label}{current && <span style={{ fontSize:10, color:'#4D4E8D', marginLeft:8 }}>← Current</span>}
              </span>
            </div>
            {i < displaySteps.length-1 && <div className="lux-tracker-line" style={{ background:done?'#5E8A72':'#E2E4E7' }} />}
          </div>
        )
      })}
    </div>
  )
}

function LiveTracking({ trackingNumber, carrier: carrierProp }) {
  if (!trackingNumber) return null
  const isUPS  = /^1Z/.test(trackingNumber)
  const isUSPS = /^(94|93|92|95)\d{20}$/.test(trackingNumber.replace(/\s/g,''))
  const carrier  = carrierProp || (isUPS ? 'UPS' : isUSPS ? 'USPS' : 'FedEx')
  const trackUrl = carrier === 'UPS' ? `https://www.ups.com/track?tracknum=${trackingNumber}` : carrier === 'USPS' ? `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${trackingNumber}` : `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`
  const colors = { UPS:{bg:'#351C15',color:'#FFB500',icon:'📦'}, FedEx:{bg:'#4D148C',color:'#FF6200',icon:'📫'}, USPS:{bg:'#004B97',color:'#fff',icon:'✉️'} }
  const c = colors[carrier]
  return (
    <div style={{ padding:'10px 16px', background:c.bg, borderTop:'1px solid rgba(255,255,255,0.1)', display:'flex', alignItems:'center', gap:10 }}>
      <span style={{ fontSize:20 }}>{c.icon}</span>
      <div style={{ flex:1 }}><div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', letterSpacing:'0.08em', marginBottom:2 }}>{carrier} TRACKING</div><div style={{ fontSize:12, fontWeight:600, color:c.color }}>{trackingNumber}</div></div>
      <a href={trackUrl} target="_blank" rel="noreferrer" style={{ background:c.color, color:c.bg, fontSize:11, fontWeight:700, padding:'7px 14px', borderRadius:8, textDecoration:'none', whiteSpace:'nowrap' }}>Track Package →</a>
    </div>
  )
}

function CalendarSync({ job }) {
  if (!job.scheduled_at) return null
  const dt    = new Date(job.scheduled_at)
  const dtEnd = new Date(dt.getTime() + 2*60*60*1000)
  const fmt   = d => d.toISOString().replace(/[-:]/g,'').split('.')[0]+'Z'
  const title = encodeURIComponent(`NSV Signing — ${job.signing_type}`)
  const loc   = encodeURIComponent(`${job.signing_address||''}, ${job.signing_city||''}, ${job.signing_state||''}`)
  const gUrl  = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(dt)}/${fmt(dtEnd)}&location=${loc}`
  const oUrl  = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${dt.toISOString()}&enddt=${dtEnd.toISOString()}&location=${loc}`
  return (
    <div style={{ display:'flex', gap:8, padding:'12px 16px', borderTop:'1px solid #E2E4E7', background:'#F5F6F7', flexWrap:'wrap' }}>
      <span style={{ fontSize:11, color:'#5C6065', marginRight:4, alignSelf:'center' }}>📅 Add to calendar:</span>
      <a href={gUrl} target="_blank" rel="noreferrer" className="lux-cal-btn">🗓 Google</a>
      <a href={oUrl} target="_blank" rel="noreferrer" className="lux-cal-btn">📧 Outlook</a>
    </div>
  )
}


// ── CLIENT DOCUMENT VAULT ─────────────────────────────────────
function ClientDocSection({ jobId }) {
  const { user } = useAuth()
  const [docs,      setDocs]      = useState([])
  const [open,      setOpen]      = useState(true)
  const [loading,   setLoading]   = useState(false)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    if (!open) return
    let active = true
    const load = () => supabase.from('job_documents').select('*').eq('job_id', jobId)
      .order('created_at', { ascending:false })
      .then(({ data }) => { if (active) { setDocs(data||[]); setLoading(false) } })
    setLoading(true)
    load()
    // 🔄 Live refresh the moment the notary uploads or changes a document
    const ch = supabase.channel(`client-docs-${jobId}`)
      .on('postgres_changes', { event:'*', schema:'public', table:'job_documents', filter:`job_id=eq.${jobId}` }, () => load())
      .subscribe()
    return () => { active = false; supabase.removeChannel(ch) }
  }, [open, jobId])

  async function uploadPackage(e) {
    const file = e.target.files[0]
    if (!file) return
    setUploading(true)
    try {
      const ext  = file.name.split('.').pop()
      const path = `${jobId}/closing-package-${Date.now()}.${ext}`
      const { error } = await supabase.storage.from('job-documents').upload(path, file, { upsert:true })
      if (error) throw error
      const { data:{ publicUrl } } = supabase.storage.from('job-documents').getPublicUrl(path)
      await supabase.from('job_documents').insert({
        job_id:jobId, uploaded_by:user?.id, role:'client',
        doc_type:'signing_package', file_name:file.name,
        file_url:publicUrl, file_size:file.size, qc_status:'approved',
      })
      await supabase.from('jobs').update({ closing_package_uploaded_at:new Date().toISOString() }).eq('id', jobId)
      toast.success('📦 Closing package uploaded!! Your notary will be notified!!')
      e.target.value = ''
      supabase.from('job_documents').select('*').eq('job_id', jobId)
        .order('created_at',{ascending:false}).then(({data}) => setDocs(data||[]))
    } catch (err) { toast.error('Upload failed: ' + err.message) }
    finally { setUploading(false) }
  }

  async function approveDoc(docId) {
    try {
      const now = new Date().toISOString()
      await supabase.from('job_documents').update({ qc_status:'approved', qc_approved_at:now, qc_approved_by:user?.id }).eq('id', docId)
      await supabase.from('jobs').update({ title_qc_status:'approved', status:'client_approved', qc_approved_at:now }).eq('id', jobId)
      // Log the advance so admin's timeline reflects the client's approval
      try { await supabase.from('job_status_events').insert({ job_id: jobId, status: 'client_approved', created_by_role: 'client', created_by: user?.id || null }) } catch (e) {}
      toast.success('✅ Scan-back approved!! Notary can now ship documents!!')
      supabase.from('job_documents').select('*').eq('job_id', jobId).order('created_at',{ascending:false}).then(({data}) => setDocs(data||[]))
    } catch (err) { toast.error(err.message) }
  }

  async function rejectDoc(docId) {
    const notes = window.prompt('Please describe what corrections are needed:')
    if (!notes) return
    try {
      const doc = docs.find(d => d.id === docId)
      const fileName = doc?.file_name || 'the scan-back'
      await supabase.from('job_documents').update({ qc_status:'rejected', qc_rejected_at:new Date().toISOString(), qc_rejected_by:user?.id, qc_correction_notes:notes }).eq('id', docId)
      await supabase.from('jobs').update({ title_qc_status:'corrections_needed', title_qc_notes:notes }).eq('id', jobId)
      // 📨 Send the correction request to the notary as a real message in their inbox
      await supabase.from('job_messages').insert({
        job_id: jobId,
        sender_id: user?.id,
        sender_role: 'client',
        sender_name: user?.user_metadata?.full_name || 'Client',
        recipient_role: 'notary',
        message: `🔧 Correction requested on "${fileName}": ${notes}`,
        read: false,
      })
      toast.success('📋 Correction request sent to the notary!!')
      supabase.from('job_documents').select('*').eq('job_id', jobId).order('created_at',{ascending:false}).then(({data}) => setDocs(data||[]))
    } catch (err) { toast.error(err.message) }
  }

  const signingPkg   = docs.filter(d => d.doc_type === 'signing_package')
  const scanBacks    = docs.filter(d => (d.doc_type === 'scan_back' || d.doc_type === 'completed_docs') && d.qc_status === 'approved')
  const pendingDocs  = docs.filter(d => (d.doc_type === 'scan_back' || d.doc_type === 'completed_docs') && d.qc_status === 'pending')
  const rejectedDocs = docs.filter(d => (d.doc_type === 'scan_back' || d.doc_type === 'completed_docs') && d.qc_status === 'rejected')

  return (
    <div style={{ borderTop:'1px solid rgba(77,78,141,0.3)', background:'#F5F6F7' }}>
      <button type="button" onClick={() => setOpen(o=>!o)} style={{ width:'100%', padding:'10px 18px', background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:12, fontWeight:600, color:'#41427a' }}>
        <span>
          📁 Document Vault
          {signingPkg.length > 0 && <span style={{ background:'#185FA5', color:'#fff', fontSize:10, padding:'1px 6px', borderRadius:10, marginLeft:6 }}>📦 Package uploaded</span>}
          {scanBacks.length > 0 && <span style={{ background:'#5E8A72', color:'#fff', fontSize:10, padding:'1px 6px', borderRadius:10, marginLeft:6 }}>✓ {scanBacks.length} scan-back{scanBacks.length > 1 ? 's' : ''}</span>}
          {pendingDocs.length > 0 && <span style={{ background:'#4D4E8D', color:'#fff', fontSize:10, padding:'1px 6px', borderRadius:10, marginLeft:6 }}>⏳ {pendingDocs.length} pending QC</span>}
        </span>
        <span style={{ color:'#A2A6AA' }}>{open?'▲':'▼'}</span>
      </button>
      {open && (
        <div style={{ padding:'0 18px 16px' }}>
          {loading && <div style={{ fontSize:12, color:'#A2A6AA', padding:'8px 0' }}>Loading...</div>}
          <div style={{ background:'#EBF4FF', border:'1px solid rgba(24,95,165,0.2)', borderRadius:8, padding:'10px 12px', marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:600, color:'#185FA5', marginBottom:6 }}>📦 CLOSING PACKAGE</div>
            {signingPkg.length > 0 ? (
              <div>
                {signingPkg.map(d => (
                  <div key={d.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
                    <span style={{ color:'#151715' }}>✅ {d.file_name}</span>
                    <span style={{ fontSize:10, color:'#5E8A72' }}>Uploaded {new Date(d.created_at).toLocaleString()}</span>
                  </div>
                ))}
                <label style={{ cursor:'pointer', marginTop:6, display:'inline-block' }}>
                  <input type="file" accept=".pdf,.zip" style={{ display:'none' }} onChange={uploadPackage} disabled={uploading} />
                  <span style={{ fontSize:10, color:'#185FA5', textDecoration:'underline', cursor:'pointer' }}>{uploading ? 'Uploading...' : '↺ Upload new version'}</span>
                </label>
              </div>
            ) : (
              <div>
                <div style={{ fontSize:11, color:'#5C6065', marginBottom:8 }}>Upload the loan signing package for your notary to download and print!!</div>
                <label style={{ cursor:uploading?'wait':'pointer' }}>
                  <input type="file" accept=".pdf,.zip" style={{ display:'none' }} onChange={uploadPackage} disabled={uploading} />
                  <span className="btn btn-primary btn-sm" style={{ fontSize:11, pointerEvents:'none', background:'#185FA5', borderColor:'#185FA5' }}>
                    {uploading ? '⏳ Uploading...' : '📦 Upload Closing Package'}
                  </span>
                </label>
              </div>
            )}
          </div>
          <div style={{ fontSize:11, fontWeight:600, color:'#151715', letterSpacing:'0.06em', marginBottom:8, marginTop:4 }}>📄 SCAN-BACKS & TITLE QC</div>
          {!loading && pendingDocs.length > 0 && (
            <div style={{ background:'#EEF0F8', border:'1px solid rgba(77,78,141,0.3)', borderRadius:8, padding:'10px 12px', marginBottom:10 }}>
              <div style={{ fontSize:11, fontWeight:600, color:'#41427a', marginBottom:8 }}>⏳ AWAITING YOUR REVIEW</div>
              {pendingDocs.map(d => (
                <div key={d.id} style={{ background:'#fff', borderRadius:8, padding:'10px 12px', marginBottom:8, border:'1px solid #EEEFF1' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:500, color:'#151715' }}>📄 {d.file_name}</div>
                      <div style={{ fontSize:10, color:'#A2A6AA' }}>{new Date(d.created_at).toLocaleString()}</div>
                    </div>
                    <a href={d.file_url} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm" style={{ fontSize:10 }}>👁️ View</a>
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <button onClick={() => approveDoc(d.id)} className="btn btn-primary btn-sm" style={{ fontSize:11, background:'#5E8A72', borderColor:'#5E8A72', flex:1 }}>✅ Approve — Good to Ship!!</button>
                    <button onClick={() => rejectDoc(d.id)} className="btn btn-outline btn-sm" style={{ fontSize:11, color:'#E24B4A', borderColor:'#E24B4A', flex:1 }}>❌ Request Corrections</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && scanBacks.length > 0 && (
            <div style={{ marginBottom:8 }}>
              {scanBacks.map(d => (
                <div key={d.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #EEEFF1' }}>
                  <div>
                    <div style={{ fontSize:13, color:'#151715', fontWeight:500 }}>📄 {d.file_name}</div>
                    <div style={{ fontSize:10, color:'#5E8A72', fontWeight:500 }}>✅ Approved {d.qc_approved_at ? new Date(d.qc_approved_at).toLocaleString() : ''}</div>
                  </div>
                  <a href={d.file_url} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm" style={{ fontSize:11 }}>⬇️ Download</a>
                </div>
              ))}
            </div>
          )}
          {!loading && rejectedDocs.length > 0 && (
            <div style={{ background:'#FDF2F2', border:'1px solid rgba(226,75,74,0.2)', borderRadius:8, padding:'10px 12px', marginBottom:8 }}>
              <div style={{ fontSize:11, fontWeight:600, color:'#C0392B', marginBottom:6 }}>❌ CORRECTIONS REQUESTED</div>
              {rejectedDocs.map(d => (
                <div key={d.id} style={{ fontSize:12, color:'#E24B4A', marginBottom:4 }}>
                  📄 {d.file_name}
                  {d.qc_correction_notes && <div style={{ fontSize:11, color:'#C0392B', marginTop:2, fontStyle:'italic' }}>📝 {d.qc_correction_notes}</div>}
                </div>
              ))}
            </div>
          )}
          {!loading && scanBacks.length === 0 && pendingDocs.length === 0 && rejectedDocs.length === 0 && signingPkg.length > 0 && (
            <div style={{ fontSize:12, color:'#A2A6AA', padding:'8px 0' }}>📄 Scan-backs will appear here once your notary uploads them!!</div>
          )}
        </div>
      )}
    </div>
  )
}


// ── NOTARY PROFILE CARD ───────────────────────────────────────
function NotaryProfileCard({ notaryId }) {
  const [notary, setNotary] = useState(null)
  useEffect(() => {
    if (!notaryId) return
    supabase.from('notaries').select('*, profiles(full_name, phone, bio, headshot_url)').eq('id', notaryId).single().then(({data}) => setNotary(data))
  }, [notaryId])
  if (!notaryId || !notary) return null
  const name     = notary.profiles?.full_name || 'Your NSV Notary'
  const phone    = notary.profiles?.phone
  const bio      = notary.profiles?.bio
  const headshot = notary.profiles?.headshot_url
  const tags = []
  if (notary.commission_state) tags.push(`📜 Commissioned in ${notary.commission_state}`)
  if (notary.years_experience) tags.push(`⭐ ${notary.years_experience} yrs experience`)
  if (notary.ron_certified)    tags.push('💻 RON Certified')
  if (notary.languages?.filter(l => l !== 'English').length > 0) tags.push('🗣️ Bilingual')
  return (
    <div style={{ padding:'14px 16px', borderTop:'1px solid #E2E4E7' }}>
      <div style={{ fontSize:10, color:'#5C6065', letterSpacing:'0.08em', marginBottom:8, fontWeight:600 }}>MEET YOUR NOTARY</div>
      <div className="lux-notary-card">
        <div style={{ width:56, height:56, borderRadius:'50%', overflow:'hidden', background:'rgba(77,78,141,0.15)', border:'2px solid rgba(77,78,141,0.3)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          {headshot ? <img src={headshot} alt={name} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : <span style={{ fontSize:24 }}>🧑‍💼</span>}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginBottom:3 }}>
            <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:17, color:'#fff', fontWeight:600 }}>{name}</div>
            {notary.is_founder && <span style={{ background:'linear-gradient(135deg,#41427a,#4D4E8D)', color:'#fff', fontSize:9, fontWeight:700, padding:'2px 8px', borderRadius:20 }}>🏅 FOUNDER</span>}
            {notary.is_ron     && <span style={{ background:'linear-gradient(135deg,#1A4A6B,#2D7AB5)', color:'#fff', fontSize:9, fontWeight:700, padding:'2px 8px', borderRadius:20 }}>💻 RON</span>}
          </div>
          {notary.is_vetted && <div className="lux-vetted-badge" style={{ marginBottom:4 }}>★ NSV Vetted</div>}
          {notary.avg_rating > 0 && <div style={{ fontSize:11, color:'#fff', marginBottom:4 }}>{"★".repeat(Math.round(notary.avg_rating))} {notary.avg_rating}/5</div>}
          {bio && <div style={{ fontSize:11, color:'rgba(255,255,255,0.6)', lineHeight:1.5, marginBottom:6 }}>{bio}</div>}
          {tags.length > 0 && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginBottom:6 }}>
              {tags.map((t,i) => <span key={i} style={{ fontSize:10, background:'rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.7)', padding:'2px 8px', borderRadius:20 }}>{t}</span>)}
            </div>
          )}
          {phone && <a href={`tel:${phone}`} style={{ fontSize:12, color:'#fff', textDecoration:'none' }}>📞 {phone}</a>}
        </div>
        {notary.vendor_number && (
          <div style={{ textAlign:'right', flexShrink:0 }}>
            <div style={{ fontSize:10, color:'rgba(255,255,255,0.35)', marginBottom:4 }}>Vendor</div>
            <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:13, color:'#fff', fontWeight:600 }}>{notary.vendor_number}</div>
          </div>
        )}
      </div>
    </div>
  )
}

function CompactOrderCard({ job: j, user }) {
  const [notary, setNotary] = useState(null)
  const [open,   setOpen]   = useState(!['completed','scanbacks_uploaded','client_approved','payment_received','notary_paid','closed','cancelled'].includes(j.status))

  useEffect(() => {
    if (!j.notary_id) return
    supabase.from('profiles').select('full_name, phone, headshot_url, notaries(is_vetted, is_ron, is_founder, vendor_number)').eq('id', j.notary_id).single().then(({ data }) => setNotary(data))
  }, [j.notary_id])

  const isCompleted = ['completed','scanbacks_uploaded','client_approved','payment_received','notary_paid','closed'].includes(j.status)
  const fileRef = j.file_number || j.client_po || null

  return (
    <div style={{ border:'1.5px solid #E2E4E7', borderRadius:14, overflow:'hidden', marginBottom:12, background:'#fff', transition:'box-shadow 0.2s, border-color 0.2s' }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow='0 4px 20px rgba(21,23,21,0.10)'; e.currentTarget.style.borderColor='rgba(77,78,141,0.4)' }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow='none'; e.currentTarget.style.borderColor='#E2E4E7' }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{ width:'100%', background:'linear-gradient(135deg,#4D4E8D,#41427a)', padding:'14px 16px', border:'none', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8, textAlign:'left' }}>
        <div style={{ flex:1 }}>
          <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:18, fontWeight:700, color:'#fff', lineHeight:1.2 }}>{j.borrower_name || 'Borrower TBD'}</div>
          {Array.isArray(j.additional_signers) && j.additional_signers.filter(s => (s?.name || '').trim()).length > 0 && (
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.8)', marginTop:3 }}>
              👥 + {j.additional_signers.filter(s => (s?.name || '').trim()).map(s => s.name).join(', ')}
            </div>
          )}
          {fileRef && <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', marginTop:2 }}>📁 File # {fileRef}</div>}
          <div style={{ fontSize:12, color:'rgba(255,255,255,0.65)', marginTop:4 }}>{j.signing_type}</div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, flexShrink:0 }}>
          <StatusRibbon status={j.status} />
          <div style={{ fontSize:10, color:'rgba(77,78,141,0.7)', fontFamily:'Plus Jakarta Sans,sans-serif' }}>{j.job_number}</div>
          <span style={{ fontSize:14, color:'rgba(255,255,255,0.35)' }}>{open ? '▲' : '▼'}</span>
        </div>
      </button>
      {open && (
        <>
          <div style={{ padding:'10px 14px', borderBottom:'1px solid #EEEFF1', display:'flex', justifyContent:'space-between', gap:12, flexWrap:'wrap', background:'#FBFAF7' }}>
            <div><div style={{ fontSize:10, color:'#A2A6AA', fontWeight:600, letterSpacing:'0.06em' }}>NSV ORDER #</div><div style={{ fontSize:12, fontWeight:600, color:'#41427a' }}>{j.job_number}</div></div>
            <div style={{ textAlign:'right' }}><div style={{ fontSize:10, color:'#A2A6AA', fontWeight:600, letterSpacing:'0.06em' }}>YOUR FILE #</div><div style={{ fontSize:12, fontWeight:600, color:'#151715' }}>{j.file_number || j.client_po || '—'}</div></div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:0, borderBottom:'1px solid #EEEFF1' }}>
            {[
              { label:'Location',    value:`${j.signing_city}, ${j.signing_state}` },
              { label:'Address',     value:j.signing_address || '—' },
              { label:'Appointment', value:j.scheduled_at ? new Date(j.scheduled_at).toLocaleString('en-US',{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'}) : 'TBD' },
            ].map((item, i) => (
              <div key={i} style={{ padding:'10px 14px', borderRight:i < 2 ? '1px solid #EEEFF1' : 'none' }}>
                <div style={{ fontSize:10, color:'#A2A6AA', fontWeight:600, letterSpacing:'0.06em', marginBottom:2 }}>{item.label.toUpperCase()}</div>
                <div style={{ fontSize:12, fontWeight:500, color:'#151715', lineHeight:1.3 }}>{item.value}</div>
              </div>
            ))}
          </div>
          <div style={{ borderBottom:'1px solid #EEEFF1' }}><LuxTracker status={j.status} /></div>
          {j.tracking_number && <LiveTracking trackingNumber={j.tracking_number} carrier={j.shipping_carrier} />}
          {j.receipt_url && (
            <a href={j.receipt_url} target="_blank" rel="noreferrer" style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 16px', borderBottom:'1px solid #EEEFF1', fontSize:12, color:'#185FA5', textDecoration:'none', fontWeight:600 }}>
              🧾 View shipping receipt →
            </a>
          )}
          {j.docs_downloaded_at && (
            <div style={{ padding:'10px 16px', background:'#F0FAF5', borderBottom:'1px solid #EEEFF1', fontSize:12, color:'#1A4A2E', fontWeight:600 }}>
              ✅ Downloaded by notary · {new Date(j.docs_downloaded_at).toLocaleString()}
            </div>
          )}
          {notary && (
            <div style={{ padding:'12px 16px', background:'#F5F6F7', borderBottom:'1px solid #EEEFF1' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:42, height:42, borderRadius:'50%', overflow:'hidden', flexShrink:0 }}>
                  {notary.headshot_url ? <img src={notary.headshot_url} alt={notary.full_name} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, fontWeight:700, color:'#4D4E8D', background:'#4D4E8D' }}>{notary.full_name?.charAt(0)}</div>}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#151715' }}>{notary.full_name}</div>
                  {notary.phone && <a href={`tel:${notary.phone}`} style={{ fontSize:11, color:'#41427a', textDecoration:'none', fontWeight:600, display:'block', marginTop:3 }}>📞 {notary.phone}</a>}
                  <div style={{ display:'flex', gap:4, marginTop:4, flexWrap:'wrap' }}>
                    {notary.notaries?.is_vetted  && <span style={{ fontSize:9, background:'#4D4E8D', color:'#fff', padding:'1px 6px', borderRadius:10, fontWeight:700 }}>★ VETTED</span>}
                    {notary.notaries?.is_ron     && <span style={{ fontSize:9, background:'#1A4A6B', color:'#fff', padding:'1px 6px', borderRadius:10, fontWeight:700 }}>💻 RON</span>}
                    {notary.notaries?.is_founder && <span style={{ fontSize:9, background:'linear-gradient(135deg,#41427a,#4D4E8D)', color:'#fff', padding:'1px 6px', borderRadius:10, fontWeight:700 }}>🏅 FOUNDER</span>}
                  </div>
                </div>
                {notary.notaries?.avg_rating > 0 && (
                  <div style={{ flexShrink:0, textAlign:'center', borderLeft:'1px solid #E2E4E7', paddingLeft:16, minWidth:80 }}>
                    <div style={{ fontSize:18, color:'#4D4E8D', letterSpacing:2 }}>{'★'.repeat(Math.round(notary.notaries.avg_rating))}</div>
                    <div style={{ fontSize:9, color:'#41427a', marginTop:2 }}>{notary.notaries.avg_rating}/5 overall</div>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* ⭐ Rate Your Notary — full field, directly below the notary info card */}
          {notary && isCompleted && j.notary_id && (
            <StarRating jobId={j.id} notaryId={j.notary_id} clientId={user?.id} />
          )}
          {/* Meet Your Notary */}
          <NotaryProfileCard notaryId={j.notary_id} />
          {/* Pre-appointment checklist */}
          {j.checklist && <ChecklistSummaryClient checklist={j.checklist} />}
          {/* Office prefs badges */}
          {j.office_prefs && Object.keys(j.office_prefs).some(k => k !== 'custom_note' && j.office_prefs[k] === true) && (
            <div style={{ padding:'8px 16px', background:'#EBF4FF', borderBottom:'1px solid #EEEFF1', fontSize:11, color:'#1A4A6B' }}>
              🏛️ {[
                j.office_prefs.blue_ink    && '🖊️ Blue Ink',
                j.office_prefs.no_staples  && '📎 No Staples',
                j.office_prefs.gps_checkin && '📍 GPS Check-In',
                j.office_prefs.borrower_id && '🪪 Borrower ID',
                j.office_prefs.call_before && '📞 Call Before',
                j.office_prefs.scan_backs  && '📄 Scan-Backs',
              ].filter(Boolean).join(' · ')}
            </div>
          )}
          {/* Document Vault */}
          <ClientDocSection jobId={j.id} />
          <CalendarSync job={j} />
          <OrderNotes jobId={j.id} initialNotes={j.order_notes} />
          <JobMessages jobId={j.id} jobNumber={j.job_number} role="client" recipientRole="admin" />
        </>
      )}
    </div>
  )
}

// ── DASHBOARD ─────────────────────────────────────────────────
function Dashboard() {
  const { user } = useAuth()
  const navigate  = useNavigate()
  const [jobs,    setJobs]    = useState([])
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return
    async function loadJobs() {
      const { data } = await supabase.from('jobs').select('*').eq('client_id', user.id).order('created_at', { ascending:false })
      setJobs(safeParseJobs(data))
    }
    Promise.all([
      supabase.from('jobs').select('*').eq('client_id', user.id).order('created_at', { ascending:false }),
      supabase.from('profiles').select('*, clients(*)').eq('id', user.id).single(),
    ]).then(([{ data:jobs }, { data:profile }]) => {
      setJobs(safeParseJobs(jobs))
      setProfile(profile)
      setLoading(false)
    })
    // 🔴 Live updates — reflect status changes (e.g. QC approval) without a manual reload
    const ch = supabase.channel(`client-jobs-${user.id}`)
      .on('postgres_changes', { event:'*', schema:'public', table:'jobs', filter:`client_id=eq.${user.id}` }, () => loadJobs())
      .subscribe()
    return () => { supabase.removeChannel(ch) }
  }, [user])

  const active      = jobs.filter(j => !['closed','cancelled','declined'].includes(j.status))
  const completed   = jobs.filter(j => j.status === 'closed')
  const outstanding = jobs.filter(j => j.invoice_sent_at && !j.client_paid_at)
  const totalSpent  = jobs.filter(j => !!j.client_paid_at).reduce((s,j) => s + (j.total_fee||0), 0)
  const recentJobs  = jobs.slice(0, 5)

  if (loading) return <div style={{ textAlign:'center', padding:60 }}><div className="spinner" style={{ margin:'auto' }} /></div>

  return (
    <>
      <style>{LUXURY_STYLES}</style>
      <div style={{ background:'linear-gradient(135deg,#4D4E8D,#41427a)', borderRadius:18, padding:'24px 28px', marginBottom:24, border:'1px solid rgba(77,78,141,0.2)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
          <div>
            <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:26, fontWeight:600, color:'#fff', marginBottom:4 }}>Welcome back, {profile?.full_name?.split(' ')[0] || 'there'}.</div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.5)' }}>{profile?.clients?.company_name || 'Notary Signing Vault, LLC.'}</div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:10, color:'rgba(255,255,255,0.35)', letterSpacing:'0.08em' }}>CLIENT #</div>
            <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:15, color:'#fff', fontWeight:600 }}>{profile?.clients?.client_number || '—'}</div>
            {profile?.is_approved && <div style={{ marginTop:6, fontSize:10, background:'rgba(45,122,79,0.2)', color:'#5BA882', border:'1px solid rgba(45,122,79,0.3)', borderRadius:20, padding:'2px 10px', display:'inline-block' }}>✓ ACTIVE</div>}
          </div>
        </div>
        {/* ✅ FIX 7 — Clickable stat cards */}
        <div className="lux-stats" style={{ marginTop:20, marginBottom:0 }}>
          <StatCard label="ACTIVE"      value={active.length}                                                           sub="all clear"  onClick={() => navigate('/client/orders?filter=active')} />
          <StatCard label="COMPLETED"   value={completed.length}                                                        sub="tap to view" onClick={() => navigate('/client/orders?filter=completed')} />
          <StatCard label="OUTSTANDING" value={`$${outstanding.reduce((s,j) => s+(j.total_fee||0),0).toLocaleString()}`} sub="all paid"   onClick={() => navigate('/client/invoices')} />
          <StatCard label="TOTAL SPENT" value={`$${totalSpent.toLocaleString()}`}                                       sub="tap to view" onClick={() => navigate('/client/invoices')} />
        </div>
      </div>

      {profile?.clients && <LoyaltyTierTracker client={profile.clients} />}

      <button className="lux-hero-btn" onClick={() => navigate('/client/new')} style={{ marginBottom:24 }} aria-label="Place a new signing request">
        <span style={{ fontSize:22 }}>✍️</span>
        <span>New Signing Request</span>
        <span style={{ fontSize:12, color:'rgba(77,78,141,0.6)', marginLeft:'auto' }}>Go to order form →</span>
      </button>

      {recentJobs.length > 0 && (
        <section className="section-card" style={{ marginBottom:24 }}>
          <div className="section-header">
            <h2 className="section-title">Recent Signings</h2>
            <button onClick={() => navigate('/client/orders')} className="btn btn-outline btn-sm" style={{ fontSize:11 }}>View All →</button>
          </div>
          <div className="section-body" style={{ padding:0 }}>
            {recentJobs.map(j => <RecentFileItem key={j.id} job={j} />)}
          </div>
        </section>
      )}

      {/* ACTIVE SIGNINGS TRACKER */}
      <section className="section-card">
        <div className="section-header">
          <h2 className="section-title">Active Signings</h2>
          <button onClick={() => navigate('/client/orders?filter=active')} className="btn btn-outline btn-sm" style={{ fontSize:11 }}>View All →</button>
        </div>
        <div className="section-body" style={{ padding:0 }}>
          {active.length === 0 ? (
            <div style={{ padding:'28px 20px', textAlign:'center', color:'#A2A6AA', fontSize:13 }}>
              ✅ No active signings right now — everything is clear!!
            </div>
          ) : (
            active.map(j => (
              <div key={j.id}
                onClick={() => navigate(`/client/jobs/${j.id}`)}
                style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 20px', borderBottom:'1px solid #EEEFF1', cursor:'pointer', transition:'background 0.15s', gap:12, flexWrap:'wrap' }}
                onMouseEnter={e => e.currentTarget.style.background='#EEF0F8'}
                onMouseLeave={e => e.currentTarget.style.background=''}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#151715', marginBottom:2 }}>{j.borrower_name || 'Borrower TBD'}</div>
                  <div style={{ fontSize:11, color:'#5C6065' }}>{j.signing_type} · {j.signing_city}, {j.signing_state}</div>
                  {j.scheduled_at && (
                    <div style={{ fontSize:11, color:'#4D4E8D', marginTop:2, fontWeight:500 }}>
                      📅 {new Date(j.scheduled_at).toLocaleString('en-US', { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' })}
                    </div>
                  )}
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                  <StatusRibbon status={j.status} />
                  <span style={{ fontSize:12, color:'#A2A6AA' }}>→</span>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* UPCOMING THIS WEEK */}
      {(() => {
        const now  = new Date()
        const week = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
        const upcoming = jobs.filter(j => {
          if (!j.scheduled_at) return false
          const d = new Date(j.scheduled_at)
          return d >= now && d <= week && !['closed','cancelled','declined'].includes(j.status)
        })
        if (upcoming.length === 0) return null
        return (
          <section className="section-card" style={{ marginTop:16 }}>
            <div className="section-header">
              <h2 className="section-title">📅 This Week</h2>
              <button onClick={() => navigate('/client/calendar')} className="btn btn-outline btn-sm" style={{ fontSize:11 }}>Full Calendar →</button>
            </div>
            <div className="section-body" style={{ padding:0 }}>
              {upcoming.map(j => (
                <div key={j.id}
                  onClick={() => navigate(`/client/jobs/${j.id}`)}
                  style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 20px', borderBottom:'1px solid #EEEFF1', cursor:'pointer', transition:'background 0.15s', gap:12 }}
                  onMouseEnter={e => e.currentTarget.style.background='#EEF0F8'}
                  onMouseLeave={e => e.currentTarget.style.background=''}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12, fontWeight:600, color:'#151715' }}>{j.borrower_name || 'TBD'} — {j.signing_type}</div>
                    <div style={{ fontSize:11, color:'#4D4E8D', fontWeight:500, marginTop:2 }}>
                      📅 {new Date(j.scheduled_at).toLocaleString('en-US', { weekday:'short', month:'short', day:'numeric', hour:'numeric', minute:'2-digit' })}
                    </div>
                  </div>
                  <StatusRibbon status={j.status} />
                </div>
              ))}
            </div>
          </section>
        )
      })()}
    </>
  )
}

// ── NEW ORDER ─────────────────────────────────────────────────
function NewOrder() {
  const { user }   = useAuth()
  const navigate   = useNavigate()
  const [searchParams] = useSearchParams()
  const [services, setServices]   = useState([])
  const [feesMap,  setFeesMap]    = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [saving,   setSaving]     = useState(false)
  const [profile,  setProfile]    = useState(null)
  const [showMsa,   setShowMsa]   = useState(false)
  const [msaName,   setMsaName]   = useState('')
  const [msaTitle,  setMsaTitle]  = useState('')
  const [msaSaving, setMsaSaving] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPos, setTooltipPos]   = useState({ top:0, left:0 })
  const tooltipRef = useRef(null)

  const EMPTY = {
    signing_type:'', sector:'', borrower_name:'', borrower_phone:'', borrower_email:'',
    signing_address:'', signing_city:'', signing_state:'', signing_zip:'',
    scheduled_at:'', language_pref:'No preference', witness_required:false,
    file_number:'', client_po:'', special_instructions:'',
    job_type: searchParams.get('type') || 'client',
    scan_backs: true,
    additional_signers: [],
  }
  const [form, setForm] = useState(() => {
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}')
      // ✅ Never restore dropdown values from draft — always show placeholders
      return { ...EMPTY, ...d, signing_type:'', sector:'', signing_state:'' }
    }
    catch { return EMPTY }
  })

  useEffect(() => {
    fetchServices().then(svcs => { setServices(svcs); setFeesMap(buildFeesMap(svcs)) })
    if (user) supabase.from('profiles').select('*, clients(*), address_book').eq('id', user.id).single().then(({ data }) => setProfile(data))
  }, [user])

  useEffect(() => { try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)) } catch {} }, [form])

  useEffect(() => {
    function handleClick(e) { if (tooltipRef.current && !tooltipRef.current.contains(e.target)) setShowTooltip(false) }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const fee = feesMap[form.signing_type] || 0

  async function submit(skipContractCheck = false) {
    if (!form.signing_type) { toast.error('Please select a signing type!!'); return }
    if (!form.borrower_name) { toast.error('Please enter the signer name!!'); return }
    if (!form.signing_address) { toast.error('Please enter the signing address!!'); return }
    if (!form.signing_city) { toast.error('Please enter the city!!'); return }
    // ⚖️ Compliance gate — must accept the Master Service Agreement before the first order
    if (!skipContractCheck && !profile?.contract_signed) { setShowMsa(true); return }
    setSaving(true)
    try {
      const jobNum = `NSV-${Date.now().toString().slice(-6)}`
      // 💰 Pull notary pay straight from Services & Pricing for the chosen service (admin can still override later)
      const svc       = services.find(s => (s.name_en || s.name) === form.signing_type)
      const totalFee  = fee || LCA_FLAT_FEE
      const notaryPay = (svc && svc.notary_pay != null) ? svc.notary_pay : Math.max(0, totalFee - LCA_FLAT_FEE)
      const lcaCut    = Math.max(0, totalFee - notaryPay)
      // 👥 Additional-witness add-on: client +$35 → notary +$25, NSV +$10
      const finalTotal  = totalFee  + (form.witness_required ? 35 : 0)
      const finalNotary = notaryPay + (form.witness_required ? 25 : 0)
      const finalLca    = lcaCut    + (form.witness_required ? 10 : 0)
      const apptStr   = form.scheduled_at ? new Date(form.scheduled_at).toLocaleString() : null
      const fullAddr  = `${form.signing_address}, ${form.signing_city}, ${form.signing_state}`
      const { error } = await supabase.from('jobs').insert({
        job_number:jobNum, client_id:user.id, signing_type:form.signing_type, sector:form.sector,
        borrower_name:form.borrower_name, borrower_phone:form.borrower_phone, borrower_email:form.borrower_email,
        signing_address:form.signing_address, signing_city:form.signing_city, signing_state:form.signing_state, signing_zip:form.signing_zip,
        scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null, language_pref:form.language_pref, witness_required:form.witness_required,
        file_number:form.file_number, client_po:form.client_po, special_instructions:form.special_instructions,
        additional_signers:(form.additional_signers || []).filter(s => (s.name || '').trim()),
        total_fee:finalTotal, notary_pay:finalNotary, lca_cut:finalLca, status:'new', job_type:form.job_type, scan_backs_required: form.job_type === 'individual' ? false : !!form.scan_backs, office_prefs:profile?.office_prefs||{},
      })
      if (error) throw error
      try { localStorage.removeItem(DRAFT_KEY) } catch {}
      await sendAdminAlert({ client_name:form.borrower_name, client_phone:form.borrower_phone, client_email:user.email, service_name:form.signing_type, amount:finalTotal, address:fullAddr, appointment:apptStr })
      await sendClientConfirmation({ client_name:profile?.full_name||form.borrower_name, client_email:user.email, service_name:form.signing_type, amount:finalTotal, job_number:jobNum, address:fullAddr, appointment:apptStr })
      setSubmitted(true)
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  async function signMsa() {
    if (!msaName.trim() || !msaTitle.trim()) { toast.error('Please type your full name and title to sign.'); return }
    setMsaSaving(true)
    try {
      const now = new Date().toISOString()
      const { error } = await supabase.from('profiles').update({
        contract_signed:true, date_contract_signed:now,
        contract_signed_name:msaName.trim(), contract_signed_title:msaTitle.trim(),
      }).eq('id', user.id)
      if (error) throw error
      setProfile(p => ({ ...(p||{}), contract_signed:true, date_contract_signed:now }))
      setShowMsa(false)
      toast.success('✅ Master Service Agreement signed — thank you!!')
      submit(true)   // proceed with the order they were placing
    } catch (err) { toast.error(err.message) }
    finally { setMsaSaving(false) }
  }

  if (submitted) return (
    <div className="lux-success-wrap">
      <style>{LUXURY_STYLES}</style>
      <div className="lux-success-ring">✓</div>
      <h1 style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:28, color:'#151715', marginBottom:12 }}>Order Received!!</h1>
      <p style={{ color:'#5C6065', fontSize:14, maxWidth:400, lineHeight:1.75, marginBottom:28 }}>Thank you!! Your signing request has been received. We'll assign a vetted notary and confirm your appointment shortly.</p>
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', justifyContent:'center' }}>
        <button onClick={() => navigate('/client')} className="btn btn-primary">Back to Dashboard</button>
        <button onClick={() => { setForm(EMPTY); setSubmitted(false) }} className="btn btn-outline">Place Another Order</button>
      </div>
    </div>
  )

  return (
    <>
      <style>{LUXURY_STYLES}</style>
      <h1 className="page-title">New Signing Request</h1>
      <p className="page-sub" style={{ marginBottom:24 }}>Fill in the details below and we'll match you with a vetted notary in your area.</p>

      {services.length > 0 && (
        <div style={{ marginBottom:20 }}>
          <QuickServicePills services={services} onSelect={svc => setForm(f => ({ ...f, signing_type: svc.name_en || svc.name }))} selected={form.signing_type} />
        </div>
      )}

      {form.signing_type && (
        <div style={{ background:'#EEF0F8', border:'1px solid rgba(77,78,141,0.3)', borderRadius:10, padding:'10px 16px', marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
          <span style={{ fontSize:12, color:'#41427a' }}>💾 Progress auto-saved — you can safely close and resume later</span>
          <button onClick={() => { setForm(EMPTY); try { localStorage.removeItem(DRAFT_KEY) } catch {} }} style={{ background:'none', border:'none', color:'#A2A6AA', cursor:'pointer', fontSize:11 }}>Clear draft</button>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:20 }}>

        {/* SIGNING DETAILS */}
        <div className="section-card">
          <div className="section-header"><h2 className="section-title">Signing Details</h2></div>
          <div className="section-body">
            <div className="lux-form-grid">

              {/* ✅ FIX 3 — Signing type placeholder */}
              <div className="lux-full">
                <label className="lux-label">SIGNING TYPE *</label>
                <select className="lux-select" value={form.signing_type} onChange={e => setForm(f => ({ ...f, signing_type: e.target.value }))}>
                  <option value="">— Select Signing Type —</option>
                  {services.map(s => <option key={s.id} value={s.name_en || s.name}>{s.name_en || s.name}</option>)}
                </select>
                {/* ✅ FIX 2 + FIX 6 — All-inclusive note + working tooltip */}
                <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:6 }}>
                  <span style={{ fontSize:11, color:'#5E8A72', fontWeight:600 }}>✅ All-inclusive price</span>
                  <div ref={tooltipRef} className="lux-tooltip-wrap">
                    <button type="button"
                      onClick={e => { const r = e.currentTarget.getBoundingClientRect(); setTooltipPos({ top: r.top - 40, left: r.left + r.width/2 }); setShowTooltip(v => !v) }}
                      onMouseEnter={e => { const r = e.currentTarget.getBoundingClientRect(); setTooltipPos({ top: r.top - 40, left: r.left + r.width/2 }); setShowTooltip(true) }}
                      onMouseLeave={() => setShowTooltip(false)}
                      style={{ background:'rgba(21,23,21,0.08)', border:'none', borderRadius:'50%', width:18, height:18, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:11, color:'#3A3D40', fontWeight:700, flexShrink:0 }}
                      aria-label="What is included in the all-inclusive price?">
                      ⓘ
                    </button>
                    {showTooltip && (
                      <div className="lux-tooltip-box" style={{ top: tooltipPos.top, left: tooltipPos.left }}>
                        Notary · Travel · Platform · Service Fees.
                      </div>
                    )}
                  </div>
                  {fee > 0 && <span style={{ fontSize:11, color:'#4D4E8D', fontWeight:600, marginLeft:4 }}>${fee}</span>}
                </div>
              </div>

              {/* ✅ FIX 3 — Sector placeholder */}
              <div className="lux-full">
                <label className="lux-label">SECTOR</label>
                <select className="lux-select" value={form.sector} onChange={e => setForm(f => ({ ...f, sector: e.target.value }))}>
                  <option value="">— Select your Sector —</option>
                  {SECTOR_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>

              {/* ✅ FIX 4 — Witness toggle for ALL sectors */}
              <div className="lux-full">
                <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', background:'#F5F6F7', borderRadius:10, border:'1px solid #E2E4E7' }}>
                  <button type="button" className="lux-toggle-track"
                    onClick={() => setForm(f => ({ ...f, witness_required: !f.witness_required }))}
                    style={{ background: form.witness_required ? '#4D4E8D' : '#E2E4E7' }}
                    aria-checked={form.witness_required} role="switch" aria-label="Additional witness required">
                    <div className="lux-toggle-thumb" style={{ left: form.witness_required ? 22 : 2 }} />
                  </button>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:'#151715' }}>👥 Additional Witness Required</div>
                    <div style={{ fontSize:11, color:'#5C6065', marginTop:2 }}>Some documents require an extra witness beyond the notary — we provide that too!!</div>
                  </div>
                </div>
              </div>

              {/* ✅ Scan-Backs toggle */}
              <div className="lux-full">
                <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', background:'#F5F6F7', borderRadius:10, border:'1px solid #E2E4E7' }}>
                  <button type="button" className="lux-toggle-track"
                    onClick={() => setForm(f => ({ ...f, scan_backs: !f.scan_backs }))}
                    style={{ background: form.scan_backs ? '#4D4E8D' : '#E2E4E7' }}
                    aria-checked={form.scan_backs} role="switch" aria-label="Scan-backs required">
                    <div className="lux-toggle-thumb" style={{ left: form.scan_backs ? 22 : 2 }} />
                  </button>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:'#151715' }}>📄 Scan-Backs Required</div>
                    <div style={{ fontSize:11, color:'#5C6065', marginTop:2 }}>Notary must upload scanned copies of all signed documents after the appointment.</div>
                  </div>
                </div>
              </div>

              <div>
                <label className="lux-label">FILE / LOAN NUMBER</label>
                <input className="lux-input" value={form.file_number} onChange={e => setForm(f => ({ ...f, file_number: e.target.value }))} placeholder="e.g. 2024-LN-00123" />
              </div>

            </div>
          </div>
        </div>

        {/* SIGNER INFO */}
        <div className="section-card">
          <div className="section-header"><h2 className="section-title">Signer Information</h2></div>
          <div className="section-body">
            {profile?.address_book?.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <label className="lux-label">QUICK-FILL FROM ADDRESS BOOK</label>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {profile.address_book.map(entry => (
                    <button key={entry.id} type="button" onClick={() => setForm(f => ({ ...f, signing_address:entry.address, signing_city:entry.city, signing_state:entry.state||'FL', signing_zip:entry.zip, borrower_name:entry.contact_name||f.borrower_name }))}
                      style={{ fontSize:11, padding:'5px 12px', borderRadius:20, border:'1px solid #4D4E8D', background:'#EEF0F8', color:'#41427a', cursor:'pointer' }}>
                      📍 {entry.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="lux-form-grid">
              {/* ✅ FIX 1 — Generic placeholders */}
              <div className="lux-full">
                <label className="lux-label">SIGNER FULL NAME *</label>
                <input className="lux-input" value={form.borrower_name} onChange={e => setForm(f => ({ ...f, borrower_name: e.target.value }))} placeholder="e.g. Jane Doe" />
              </div>
              <div>
                <label className="lux-label">SIGNER PHONE</label>
                <input className="lux-input" value={form.borrower_phone} onChange={e => setForm(f => ({ ...f, borrower_phone: e.target.value }))} placeholder="e.g. (555) 123-4567" type="tel" />
              </div>
              <div>
                <label className="lux-label">SIGNER EMAIL</label>
                <input className="lux-input" value={form.borrower_email} onChange={e => setForm(f => ({ ...f, borrower_email: e.target.value }))} placeholder="e.g. jane@email.com" type="email" />
              </div>
              <div className="lux-full">
                <label className="lux-label">SIGNING ADDRESS *</label>
                <AddressAutocomplete className="lux-input" value={form.signing_address}
                  onChange={v => setForm(f => ({ ...f, signing_address: v }))}
                  onSelect={({ address, city, state, zip }) => setForm(f => ({ ...f, signing_address:address, signing_city:city, signing_state:state||f.signing_state, signing_zip:zip }))}
                  placeholder="Start typing the signing address..." />
              </div>
              <div>
                <label className="lux-label">CITY *</label>
                <input className="lux-input" value={form.signing_city} onChange={e => setForm(f => ({ ...f, signing_city: e.target.value }))} placeholder="City" />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label className="lux-label">STATE</label>
                  <select className="lux-select" value={form.signing_state} onChange={e => setForm(f => ({ ...f, signing_state: e.target.value }))}>
                    <option value="">— Select a State —</option>
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="lux-label">ZIP</label>
                  <input className="lux-input" value={form.signing_zip} onChange={e => setForm(f => ({ ...f, signing_zip: e.target.value.replace(/\D/g,'') }))} placeholder="Zip Code" maxLength={5} />
                  <div style={{ fontSize:10, color:'#41427a', marginTop:3 }}>✓ Please verify this ZIP matches the address</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ADDITIONAL SIGNERS */}
        <div className="section-card">
          <div className="section-header">
            <h2 className="section-title">Additional Signers</h2>
            <span style={{ fontSize:11, color:'#A2A6AA' }}>Optional</span>
          </div>
          <div className="section-body">
            <p style={{ fontSize:12, color:'#5C6065', marginBottom:14 }}>
              Some transactions require more than one signer. Add additional signers here.
            </p>
            {(form.additional_signers || []).map((signer, idx) => (
              <div key={idx} style={{ background:'#F5F6F7', borderRadius:10, padding:16, marginBottom:12, border:'1px solid #E2E4E7' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#151715' }}>Signer #{idx + 2}</div>
                  <button type="button" onClick={() => setForm(f => ({ ...f, additional_signers: f.additional_signers.filter((_, i) => i !== idx) }))}
                    style={{ background:'none', border:'none', color:'#E24B4A', cursor:'pointer', fontSize:13, fontWeight:600 }}>✕ Remove</button>
                </div>
                <div className="lux-form-grid">
                  <div className="lux-full">
                    <label className="lux-label">FULL NAME</label>
                    <input className="lux-input" value={signer.name || ''} onChange={e => setForm(f => ({ ...f, additional_signers: f.additional_signers.map((s, i) => i === idx ? { ...s, name: e.target.value } : s) }))} placeholder="e.g. John Doe" />
                  </div>
                  <div>
                    <label className="lux-label">PHONE</label>
                    <input className="lux-input" value={signer.phone || ''} onChange={e => setForm(f => ({ ...f, additional_signers: f.additional_signers.map((s, i) => i === idx ? { ...s, phone: e.target.value } : s) }))} placeholder="(555) 123-4567" type="tel" />
                  </div>
                  <div>
                    <label className="lux-label">EMAIL</label>
                    <input className="lux-input" value={signer.email || ''} onChange={e => setForm(f => ({ ...f, additional_signers: f.additional_signers.map((s, i) => i === idx ? { ...s, email: e.target.value } : s) }))} placeholder="john@email.com" type="email" />
                  </div>
                  <div className="lux-full" style={{ marginTop:4 }}>
                    <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', fontSize:13, color:'#151715', fontWeight:600 }}>
                      <input type="checkbox" checked={!!signer.separate_address}
                        onChange={e => setForm(f => ({ ...f, additional_signers: f.additional_signers.map((s, i) => i === idx ? { ...s, separate_address: e.target.checked } : s) }))}
                        style={{ width:18, height:18, cursor:'pointer', accentColor:'#4D4E8D' }} />
                      📦 Ship to a different address for this signer
                    </label>
                    <div style={{ fontSize:11, color:'#5C6065', marginTop:4, marginLeft:28 }}>Use this if this signer is in another state and signs at their own location.</div>
                  </div>
                  {signer.separate_address && (
                    <>
                      <div className="lux-full">
                        <label className="lux-label">SIGNER'S ADDRESS</label>
                        <input className="lux-input" value={signer.address || ''} onChange={e => setForm(f => ({ ...f, additional_signers: f.additional_signers.map((s, i) => i === idx ? { ...s, address: e.target.value } : s) }))} placeholder="123 Main St" />
                      </div>
                      <div>
                        <label className="lux-label">CITY</label>
                        <input className="lux-input" value={signer.city || ''} onChange={e => setForm(f => ({ ...f, additional_signers: f.additional_signers.map((s, i) => i === idx ? { ...s, city: e.target.value } : s) }))} placeholder="City" />
                      </div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                        <div>
                          <label className="lux-label">STATE</label>
                          <select className="lux-select" value={signer.state || ''} onChange={e => setForm(f => ({ ...f, additional_signers: f.additional_signers.map((s, i) => i === idx ? { ...s, state: e.target.value } : s) }))}>
                            <option value="">— State —</option>
                            {STATES.map(st => <option key={st} value={st}>{st}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="lux-label">ZIP</label>
                          <input className="lux-input" value={signer.zip || ''} onChange={e => setForm(f => ({ ...f, additional_signers: f.additional_signers.map((s, i) => i === idx ? { ...s, zip: e.target.value.replace(/\D/g,'') } : s) }))} placeholder="Zip" maxLength={5} />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
            <button type="button"
              onClick={() => setForm(f => ({ ...f, additional_signers: [...(f.additional_signers || []), { name:'', phone:'', email:'' }] }))}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 18px', background:'#F5F6F7', border:'1.5px dashed #4D4E8D', borderRadius:10, cursor:'pointer', fontSize:13, fontWeight:600, color:'#41427a', width:'100%', justifyContent:'center', transition:'all 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.background='#EEF0F8'}
              onMouseLeave={e => e.currentTarget.style.background='#F5F6F7'}>
              👤 Add Another Signer
            </button>
          </div>
        </div>

        {/* SCHEDULING */}
        <div className="section-card">
          <div className="section-header"><h2 className="section-title">Scheduling & Preferences</h2></div>
          <div className="section-body">
            <div className="lux-form-grid">
              <div className="lux-full">
                <label className="lux-label">PREFERRED DATE & TIME</label>
                <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                  <div style={{ flex:'1 1 200px', minWidth:0 }}>
                    <div style={{ fontSize:10, color:'#A2A6AA', fontWeight:600, letterSpacing:'0.06em', marginBottom:6 }}>📅 DATE</div>
                    <input
                      className="lux-input"
                      type="date"
                      value={form.scheduled_at ? form.scheduled_at.split('T')[0] : ''}
                      onChange={e => setForm(f => {
                        const time = (f.scheduled_at && f.scheduled_at.includes('T')) ? f.scheduled_at.split('T')[1] : '12:00'
                        return { ...f, scheduled_at: e.target.value ? `${e.target.value}T${time}` : '' }
                      })}
                    />
                  </div>
                  <div style={{ flex:'1 1 200px', minWidth:0 }}>
                    <div style={{ fontSize:10, color:'#A2A6AA', fontWeight:600, letterSpacing:'0.06em', marginBottom:6 }}>🕒 TIME</div>
                    <input
                      className="lux-input"
                      type="time"
                      value={form.scheduled_at && form.scheduled_at.includes('T') ? form.scheduled_at.split('T')[1].slice(0,5) : ''}
                      onChange={e => setForm(f => {
                        const date = f.scheduled_at ? f.scheduled_at.split('T')[0] : new Date().toISOString().split('T')[0]
                        return { ...f, scheduled_at: e.target.value ? `${date}T${e.target.value}` : '' }
                      })}
                    />
                  </div>
                </div>
                <div style={{ fontSize:10, color:'#A2A6AA', marginTop:8 }}>Leave blank for flexible / ASAP scheduling</div>
              </div>
              <div className="lux-full">
                <label className="lux-label">LANGUAGE PREFERENCE</label>
                <select className="lux-select" value={form.language_pref} onChange={e => setForm(f => ({ ...f, language_pref: e.target.value }))}>
                  {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div className="lux-full">
                <label className="lux-label">SPECIAL INSTRUCTIONS</label>
                <textarea className="lux-input" value={form.special_instructions} onChange={e => setForm(f => ({ ...f, special_instructions: e.target.value }))}
                  placeholder="e.g. Please call 30 minutes before arrival. Gate code is #1234. Signer is elderly — please be patient."
                  rows={3} style={{ resize:'vertical' }} />
              </div>
            </div>
          </div>
        </div>

        {/* ORDER SUMMARY */}
        {form.signing_type && (
          <div style={{ background:'linear-gradient(135deg,#4D4E8D,#41427a)', borderRadius:16, padding:'20px 24px', border:'1px solid rgba(77,78,141,0.25)' }}>
            <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:18, color:'#fff', marginBottom:14, fontWeight:600 }}>Order Summary</div>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'rgba(255,255,255,0.7)', marginBottom:8 }}>
              <span>{form.signing_type}</span>
              <span style={{ color:'#fff', fontWeight:600 }}>{fee > 0 ? `$${fee}` : 'Custom Quote'}</span>
            </div>
            {form.witness_required && (
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'rgba(255,255,255,0.7)', marginBottom:8 }}>
                <span>👥 Additional Witness</span>
                <span style={{ color:'#fff', fontWeight:600 }}>+$35</span>
              </div>
            )}
            <div style={{ borderTop:'1px solid rgba(77,78,141,0.2)', paddingTop:10, marginTop:8 }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:14, fontWeight:600, color:'#fff' }}>
                <span>Total</span>
                <div style={{ textAlign:'right' }}>
                  <span style={{ color:'#4D4E8D' }}>{fee > 0 ? `$${fee + (form.witness_required ? 35 : 0)}` : 'TBD'}</span>
                  <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', marginTop:2 }}>✅ All-inclusive · No surprises</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ✅ FIX 5 — Submit button nowrap on mobile */}
        <button onClick={() => submit()} disabled={saving || !form.signing_type} className="btn btn-primary"
          style={{ width:'100%', padding:'16px', fontSize:16, fontWeight:700, borderRadius:12, whiteSpace:'nowrap' }}>
          {saving ? 'Placing Your Order...' : '✍️ Submit Order'}
        </button>

        {showMsa && (
          <div onClick={() => !msaSaving && setShowMsa(false)} style={{ position:'fixed', inset:0, background:'rgba(21,23,21,0.55)', display:'flex', alignItems:'center', justifyContent:'center', padding:16, zIndex:9999 }}>
            <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:16, maxWidth:560, width:'100%', maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ padding:'22px 24px 14px', borderBottom:'1px solid #E2E4E7' }}>
                <h2 style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:24, color:'#151715', margin:0 }}>⚖️ Master Service Agreement</h2>
                <p style={{ fontSize:13, color:'#5C6065', margin:'6px 0 0' }}>Please review and accept before placing your first order.</p>
              </div>
              <div style={{ padding:'16px 24px', overflowY:'auto', flex:1, fontSize:14, lineHeight:1.7, color:'#3A382F' }}>
                {/* 📌 Nancy: replace the text below with your official MSA. The signature capture works regardless of the wording. */}
                <p style={{ marginTop:0 }}>This Master Service Agreement ("Agreement") is entered into between <strong>Notary Signing Vault, LLC. DBA Nationwide Notary Professionals Network</strong> ("Company") and the undersigned client ("Client").</p>
                <p><strong>1. Services.</strong> Company coordinates mobile and remote notary and signing services through its national network of vetted notaries, as requested by Client through this platform.</p>
                <p><strong>2. Fees &amp; Payment.</strong> Client agrees to pay the fees presented at the time of each order. Invoices are due upon receipt unless otherwise agreed in writing.</p>
                <p><strong>3. Independent Contractors.</strong> Notaries in the network are independent professionals. Company coordinates assignments and quality but does not employ the notaries.</p>
                <p><strong>4. Confidentiality.</strong> Both parties agree to protect the confidentiality of borrower and transaction information and to handle all documents in compliance with applicable law.</p>
                <p><strong>5. Compliance.</strong> Client is responsible for providing accurate signing instructions and lawful documents. Company will perform services in a professional, compliant manner.</p>
                <p><strong>6. Limitation of Liability.</strong> Company's liability for any claim arising from services is limited to the fees paid for the specific order giving rise to the claim.</p>
                <p><strong>7. Term &amp; Termination.</strong> This Agreement remains in effect while Client uses the platform and may be terminated by either party with written notice.</p>
                <p style={{ marginBottom:0 }}>By typing your full name and title below, you acknowledge that you have read, understood, and agree to this Agreement on behalf of your organization, and that this constitutes a legally binding electronic signature.</p>
              </div>
              <div style={{ padding:'14px 24px 22px', borderTop:'1px solid #E2E4E7' }}>
                <label style={{ fontSize:12, fontWeight:600, color:'#151715', display:'block', marginBottom:4 }}>Full Legal Name *</label>
                <input value={msaName} onChange={e => setMsaName(e.target.value)} placeholder="e.g., Jane A. Smith"
                  style={{ width:'100%', padding:'12px', fontSize:15, border:'1px solid #D9D6CD', borderRadius:10, marginBottom:12, minHeight:48, boxSizing:'border-box' }} />
                <label style={{ fontSize:12, fontWeight:600, color:'#151715', display:'block', marginBottom:4 }}>Title *</label>
                <input value={msaTitle} onChange={e => setMsaTitle(e.target.value)} placeholder="e.g., Escrow Officer, Owner"
                  style={{ width:'100%', padding:'12px', fontSize:15, border:'1px solid #D9D6CD', borderRadius:10, marginBottom:16, minHeight:48, boxSizing:'border-box' }} />
                <div style={{ display:'flex', gap:12 }}>
                  <button onClick={() => setShowMsa(false)} disabled={msaSaving} className="btn btn-outline"
                    style={{ flex:1, minHeight:48, borderRadius:10 }}>Cancel</button>
                  <button onClick={signMsa} disabled={msaSaving || !msaName.trim() || !msaTitle.trim()} className="btn btn-primary"
                    style={{ flex:2, minHeight:48, borderRadius:10, fontWeight:700 }}>
                    {msaSaving ? 'Signing...' : '✍️ I Agree & Sign'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

// ── MY ORDERS ─────────────────────────────────────────────────
function MyOrders() {
  const { user } = useAuth()
  const [jobs,    setJobs]    = useState([])
  const [loading, setLoading] = useState(true)
  const [filter,  setFilter]  = useState('active')
  const [search,  setSearch]  = useState('')
  const [searchParams] = useSearchParams()

  useEffect(() => { const f = searchParams.get('filter'); if (f) setFilter(f) }, [searchParams])

  useEffect(() => {
    if (!user) return
    let active = true
    const loadOrders = () => {
      supabase.from('jobs').select('*').eq('client_id', user.id).order('created_at', { ascending:false })
        .then(({ data }) => { if (active) { setJobs(safeParseJobs(data)); setLoading(false) } })
    }
    loadOrders()
    // 🔴 Real-time — refresh My Orders the moment any of this client's jobs change
    const ch = supabase.channel('client-my-orders-realtime')
      .on('postgres_changes', { event:'*', schema:'public', table:'jobs', filter:`client_id=eq.${user.id}` }, loadOrders)
      .subscribe()
    return () => { active = false; supabase.removeChannel(ch) }
  }, [user])

  const filtered = jobs.filter(j => {
    const matchStatus = filter === 'all' ? true
      : filter === 'active' ? !['closed','cancelled','declined'].includes(j.status)
      : filter === 'completed' ? j.status === 'closed'
      : j.status === filter
    const matchSearch = !search || j.borrower_name?.toLowerCase().includes(search.toLowerCase()) || j.job_number?.includes(search)
    return matchStatus && matchSearch
  })

  if (loading) return <div style={{ textAlign:'center', padding:60 }}><div className="spinner" style={{ margin:'auto' }} /></div>

  return (
    <>
      <style>{LUXURY_STYLES}</style>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <h1 className="page-title" style={{ margin:0 }}>My Orders</h1>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or job #..." style={{ padding:'8px 14px', border:'1.5px solid #D7DADE', borderRadius:10, fontSize:13, minWidth:200 }} />
      </div>
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        {[{value:'all',label:'All Orders'},{value:'active',label:'🟢 Active'},{value:'completed',label:'✅ Completed'},{value:'cancelled',label:'❌ Cancelled'}].map(f => (
          <button key={f.value} onClick={() => setFilter(f.value)}
            style={{ fontSize:12, padding:'7px 14px', borderRadius:20, border:'1.5px solid', cursor:'pointer', fontWeight:filter===f.value?700:400,
              background:filter===f.value?'#151715':'#fff', color:filter===f.value?'#4D4E8D':'#5C6065', borderColor:filter===f.value?'#151715':'#E2E4E7' }}>
            {f.label}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'40px 0', color:'#A2A6AA', fontSize:13 }}>{filter === 'active' ? '✅ No active orders right now!!' : 'No orders found!!'}</div>
      ) : (
        filtered.map(j => <CompactOrderCard key={j.id} job={j} user={user} />)
      )}
    </>
  )
}

// ✅ FIX 9 — All Orders comprehensive table
function AllOrders() {
  const { user } = useAuth()
  const navigate  = useNavigate()
  const [jobs,    setJobs]    = useState([])
  const [loading, setLoading] = useState(true)
  const [search,  setSearch]  = useState('')

  useEffect(() => {
    if (!user) return
    supabase.from('jobs').select('*').eq('client_id', user.id).order('created_at', { ascending:false })
      .then(({ data }) => { setJobs(safeParseJobs(data)); setLoading(false) })
  }, [user])

  const filtered = jobs.filter(j =>
    !search || j.borrower_name?.toLowerCase().includes(search.toLowerCase()) ||
    j.job_number?.includes(search) || j.signing_type?.toLowerCase().includes(search.toLowerCase()) ||
    j.signing_city?.toLowerCase().includes(search.toLowerCase())
  )

  if (loading) return <div style={{ textAlign:'center', padding:60 }}><div className="spinner" style={{ margin:'auto' }} /></div>

  return (
    <>
      <style>{LUXURY_STYLES}</style>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <h1 className="page-title" style={{ margin:0 }}>All Orders</h1>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, job #, type, or city..." style={{ padding:'8px 14px', border:'1.5px solid #D7DADE', borderRadius:10, fontSize:13, minWidth:240 }} />
      </div>
      <div style={{ fontSize:12, color:'#5C6065', marginBottom:16 }}>{filtered.length} order{filtered.length !== 1 ? 's' : ''} found</div>
      <div className="section-card" style={{ padding:0 }}>
        <div className="lux-table-wrap">
          <table className="data-table">
            <thead><tr><th>Job #</th><th>Service</th><th>Signer</th><th>Location</th><th>Date</th><th>Amount</th><th>Status</th></tr></thead>
            <tbody>
              {!loading && filtered.length === 0 && <tr><td colSpan={7} style={{ textAlign:'center', color:'#A2A6AA', padding:24 }}>No orders found</td></tr>}
              {filtered.map(j => (
                <tr key={j.id} style={{ cursor:'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background='#EEF0F8'}
                  onMouseLeave={e => e.currentTarget.style.background=''}>
                  <td style={{ fontWeight:600, color:'#4D4E8D', fontFamily:'Plus Jakarta Sans,sans-serif' }}>{j.job_number}</td>
                  <td style={{ fontSize:12 }}>{j.signing_type}</td>
                  <td><div style={{ fontSize:12, fontWeight:500 }}>{j.borrower_name||'—'}</div>{j.borrower_phone && <div style={{ fontSize:11, color:'#41427a' }}>📞 {j.borrower_phone}</div>}</td>
                  <td style={{ fontSize:12 }}>{j.signing_city}, {j.signing_state}</td>
                  <td style={{ fontSize:12 }}>{j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : '—'}</td>
                  <td style={{ fontWeight:600, color:'#5E8A72', fontSize:13 }}>${j.total_fee||0}</td>
                  <td><StatusRibbon status={j.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

// ✅ FIX 8 — Full Calendar Page
function CalendarPage() {
  const { user } = useAuth()
  const [jobs,    setJobs]    = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return
    supabase.from('jobs').select('*').eq('client_id', user.id).order('scheduled_at', { ascending:true })
      .then(({ data }) => { setJobs(safeParseJobs(data)); setLoading(false) })
  }, [user])

  if (loading) return <div style={{ textAlign:'center', padding:60 }}><div className="spinner" style={{ margin:'auto' }} /></div>

  return (
    <>
      <style>{LUXURY_STYLES}</style>
      <h1 className="page-title">Signings Calendar</h1>
      <p className="page-sub" style={{ marginBottom:24 }}>Your upcoming and past signing appointments at a glance.</p>
      <div className="section-card" style={{ marginBottom:16 }}>
        <div className="section-body"><SigningsCalendar jobs={jobs} /></div>
      </div>
      <div className="section-card">
        <div className="section-header"><h2 className="section-title">Upcoming Signings</h2></div>
        <div className="section-body" style={{ padding:0 }}>
          {jobs.filter(j => j.scheduled_at && new Date(j.scheduled_at) >= new Date()).length === 0
            ? <div style={{ padding:'24px 20px', color:'#A2A6AA', fontSize:13, textAlign:'center' }}>No upcoming signings scheduled.</div>
            : jobs.filter(j => j.scheduled_at && new Date(j.scheduled_at) >= new Date()).map(j => <RecentFileItem key={j.id} job={j} />)
          }
        </div>
      </div>
    </>
  )
}

function Invoices() {
  const { user } = useAuth()
  const [jobs,    setJobs]    = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return
    supabase.from('jobs').select('*').eq('client_id', user.id).in('status',['completed','scanbacks_uploaded','client_approved','payment_received','notary_paid','closed']).order('created_at',{ascending:false})
      .then(({ data }) => { setJobs(safeParseJobs(data)); setLoading(false) })
  }, [user])

  const total = jobs.reduce((s,j) => s+(j.total_fee||0), 0)
  const paid  = jobs.filter(j => !!j.client_paid_at).reduce((s,j) => s+(j.total_fee||0), 0)
  const due   = total - paid

  if (loading) return <div style={{ textAlign:'center', padding:60 }}><div className="spinner" style={{ margin:'auto' }} /></div>

  return (
    <>
      <style>{LUXURY_STYLES}</style>
      <h1 className="page-title">Invoices</h1>
      <div className="lux-stats" style={{ marginBottom:20 }}>
        <StatCard label="TOTAL BILLED"   value={`$${total.toLocaleString()}`} sub="all time"  onClick={undefined} />
        <StatCard label="TOTAL PAID"     value={`$${paid.toLocaleString()}`}  sub="confirmed" onClick={undefined} />
        <StatCard label="OUTSTANDING"    value={`$${due.toLocaleString()}`}   sub="due"       onClick={undefined} />
        <StatCard label="TOTAL SIGNINGS" value={jobs.length}                  sub="completed" onClick={undefined} />
      </div>
      <div className="section-card" style={{ padding:0 }}>
        <div className="lux-table-wrap">
          <table className="data-table">
            <thead><tr><th>Job #</th><th>Service</th><th>Date</th><th>Amount</th><th>Status</th><th>Pay</th></tr></thead>
            <tbody>
              {jobs.length === 0 && <tr><td colSpan={6} style={{ textAlign:'center', color:'#A2A6AA', padding:24 }}>No invoices yet!!</td></tr>}
              {jobs.map(j => (
                <tr key={j.id}>
                  <td style={{ fontWeight:600, color:'#4D4E8D', fontFamily:'Plus Jakarta Sans,sans-serif' }}>{j.job_number}</td>
                  <td style={{ fontSize:12 }}>{j.signing_type}</td>
                  <td style={{ fontSize:12 }}>{new Date(j.created_at).toLocaleString()}</td>
                  <td style={{ fontWeight:600, color:'#151715' }}>${j.total_fee||0}</td>
                  <td><StatusRibbon status={j.status} /></td>
                  <td>
                    {!j.client_paid_at
                      ? <a href={STRIPE_PAYMENT_LINK} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm" style={{ fontSize:11 }}>Pay Now →</a>
                      : <span style={{ fontSize:11, color:'#5E8A72', fontWeight:600 }}>✓ Paid</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

function CompanyProfile() {
  const { user } = useAuth()
  const [profile, setProfile] = useState(null)
  const [saving,  setSaving]  = useState(false)
  const [saved,   setSaved]   = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return
    supabase.from('profiles').select('*, clients(*)').eq('id', user.id).single().then(({ data }) => { setProfile(data); setLoading(false) })
  }, [user])

  async function save() {
    setSaving(true)
    try {
      await supabase.from('profiles').update({ full_name:profile.full_name, phone:profile.phone }).eq('id', user.id)
      setSaved(true)
      toast.success('Profile saved!!')
      setTimeout(() => setSaved(false), 3000)
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  if (loading) return <div style={{ textAlign:'center', padding:60 }}><div className="spinner" style={{ margin:'auto' }} /></div>

  return (
    <>
      <style>{LUXURY_STYLES}</style>
      <h1 className="page-title">Company Profile</h1>
      <div className="section-card" style={{ marginBottom:20 }}>
        <div className="section-header"><h2 className="section-title">Your Information</h2></div>
        <div className="section-body">
          <div className="lux-form-grid">
            <div><label className="lux-label">FULL NAME</label><input className="lux-input" value={profile?.full_name||''} onChange={e => setProfile(p => ({ ...p, full_name:e.target.value }))} placeholder="Jane Doe" /></div>
            <div><label className="lux-label">PHONE</label><input className="lux-input" value={profile?.phone||''} onChange={e => setProfile(p => ({ ...p, phone:e.target.value }))} placeholder="(555) 123-4567" type="tel" /></div>
            <div className="lux-full"><label className="lux-label">EMAIL</label><input className="lux-input" value={profile?.email||user?.email||''} disabled style={{ opacity:0.6 }} /></div>
          </div>
          <button onClick={save} disabled={saving||saved} className="btn btn-primary" style={{ marginTop:16, fontSize:12 }}>{saving?'Saving...':saved?'✓ Saved!!':'Save Profile'}</button>
        </div>
      </div>
      <div className="section-card" style={{ marginBottom:20 }}>
        <div className="section-header"><h2 className="section-title">Office Preferences</h2></div>
        <div className="section-body">
          <p style={{ fontSize:12, color:'#5C6065', marginBottom:16 }}>These preferences apply to all your signing orders automatically!!</p>
          <OfficePrefs userId={user.id} currentPrefs={profile?.office_prefs} />
        </div>
      </div>
      <div className="section-card">
        <div className="section-header"><h2 className="section-title">Address Book</h2></div>
        <div className="section-body">
          <p style={{ fontSize:12, color:'#5C6065', marginBottom:16 }}>Save your frequently used signing locations for quick order placement!!</p>
          <AddressBook userId={user.id} currentBook={profile?.address_book} />
        </div>
      </div>
    </>
  )
}

// ── MAIN EXPORT ───────────────────────────────────────────────
export default function ClientPortal() {
  return (
    <PortalLayout sidebarItems={NAV}>
      <style>{LUXURY_STYLES}</style>
      <Routes>
        <Route index             element={<Dashboard />} />
        <Route path="new"        element={<NewOrder />} />
        <Route path="orders"     element={<MyOrders />} />
        <Route path="all-orders" element={<AllOrders />} />
        <Route path="calendar"   element={<CalendarPage />} />
        <Route path="invoices"   element={<Invoices />} />
        <Route path="profile"    element={<CompanyProfile />} />
        <Route path="jobs/:id"   element={<ClientJobDetail />} />
      </Routes>
    </PortalLayout>
  )
}
